import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { useParams, useNavigate } from 'react-router-dom'
import WatermarkOverridePanel from './WatermarkOverridePanel'
import FeedTrashPanel from './FeedTrashPanel'
import PostVariationsSettingsPanel from '../../../components/PostVariationsSettingsPanel'
import {
  Account,
  FacebookProfile,
  ClientLog,
  Invoice,
  getAccountStatus,
  getAccountStatusLabel,
  getAccountStatusColor
} from './core/_models'
import {
  getAccountDetail,
  updateAccount,
  extendTrial,
  chargeCustomAmount,
  toggleGroupScheduling,
  toggleXpostSpread,
  toggleAnonPostLimited,
  toggleSeedGroups,
  togglePaypal,
  cancelPaypalSubscription,
  toggleStatsRealNumbers,
  toggleMarketplaceCheckbox,
  toggleApiAccess,
  toggleEmailVerified,
  toggleSchedule,
  toggleAccountActivation,
  getAccountDisableImpact,
  disableAccount,
  DisableImpact,
  toggleProfileStatus,
  addAccountLog,
  deleteAccountLog,
  loginAsUser,
  forceLogoutUser,
  sendWelcomeMessage,
  toggleAffiliate,
  setBulkProfiles,
  getAccountSlots,
  addAccountSlot,
  removeAccountSlot,
  getPackageTimeline,
  getPlanPayments,
  transferCampaigns,
  getLibrarySharing,
  addLibrarySharing,
  removeLibrarySharing,
  syncLibrarySharing,
  getChildAccounts,
  addChildAccount,
  removeChildAccount,
  updateChildAccount,
  updateBillingDay,
  syncChildTrials,
  togglePostFooter,
  setPostFooterPosition,
  updateProfilePostsPerDay
} from './core/_requests'
import { getMeetings, rescheduleMeeting } from '../meetings/core/_requests'
import { getAdminToken } from '../auth/_requests'
import SupportChat from './SupportChat'
import { AdminMeeting, AccountRef, MEETING_TYPE_LABELS, MEETING_TYPE_COLORS } from '../meetings/core/_models'
import moment from 'moment'
import 'moment/locale/he'
moment.locale('he')

// Light mode color scheme
const colors = {
  bgPrimary: '#f5f8fa',
  bgSecondary: '#ffffff',
  bgTertiary: '#e8ecef',
  textPrimary: '#1e1e2d',
  textSecondary: '#5e6278',
  textMuted: '#a1a5b7',
  border: '#e4e6ef',
  borderLight: '#eff2f5',
}

const AccountDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // State
  const [account, setAccount] = useState<Account | null>(null)
  const [profiles, setProfiles] = useState<FacebookProfile[]>([])
  const [logs, setLogs] = useState<ClientLog[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Edit states
  const [editMode, setEditMode] = useState(false)
  const [editData, setEditData] = useState<Partial<Account>>({})

  // Subscription period modal
  const [showTrialModal, setShowTrialModal] = useState(false)
  const [trialDays, setTrialDays] = useState(30)
  const [trialDate, setTrialDate] = useState('')
  const [trialMode, setTrialMode] = useState<'days' | 'date'>('days')
  const [trialReason, setTrialReason] = useState('')

  // One-off card charge (amount + reason, no subscription time granted)
  const [showChargeModal, setShowChargeModal] = useState(false)
  const [chargeAmount, setChargeAmount] = useState('')
  const [chargeReason, setChargeReason] = useState('')
  const [chargeInstallments, setChargeInstallments] = useState(1)
  const [charging, setCharging] = useState(false)
  const [chargeResult, setChargeResult] = useState<{ok: boolean; message: string} | null>(null)

  // Inline edit of the monthly billing amount (logged to הערות ולוג server-side)
  const [editingMonthly, setEditingMonthly] = useState(false)
  const [monthlyDraft, setMonthlyDraft] = useState('')
  const [savingMonthly, setSavingMonthly] = useState(false)

  // Log form
  const [newLogSubject, setNewLogSubject] = useState('')
  const [newLogDescription, setNewLogDescription] = useState('')

  // Slot management
  const [slots, setSlots] = useState<any>(null)
  const [packageChanges, setPackageChanges] = useState<any[]>([])
  const [planPayments, setPlanPayments] = useState<any[]>([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [showAddSlotModal, setShowAddSlotModal] = useState(false)
  const [addSlotType, setAddSlotType] = useState<'profile' | 'subprofile'>('profile')
  const [addSlotFree, setAddSlotFree] = useState(true)
  const [addSlotReason, setAddSlotReason] = useState('')
  const [addingSlot, setAddingSlot] = useState(false)

  // Meetings
  const [accountMeetings, setAccountMeetings] = useState<AdminMeeting[]>([])
  const [reschedulingMeetingId, setReschedulingMeetingId] = useState<string | null>(null)
  const [rescheduleDate, setRescheduleDate] = useState('')

  // Restore mode toggle for admin login
  const [restoreMode, setRestoreMode] = useState(false)

  // Library sharing
  const [sharedAccounts, setSharedAccounts] = useState<any[]>([])
  const [allAccountsForSharing, setAllAccountsForSharing] = useState<any[]>([])
  const [sharingSearch, setSharingSearch] = useState('')
  const [sharingDropdownOpen, setSharingDropdownOpen] = useState(false)
  const [addingSharing, setAddingSharing] = useState<string | null>(null)
  const [syncingLibraries, setSyncingLibraries] = useState(false)
  const sharingRef = useRef<HTMLDivElement>(null)

  // Child accounts / billing
  const [childAccounts, setChildAccounts] = useState<any[]>([])
  const [childSearch, setChildSearch] = useState('')
  const [childDropdownOpen, setChildDropdownOpen] = useState(false)
  const [addingChild, setAddingChild] = useState<string | null>(null)
  const [billingDay, setBillingDay] = useState(1)
  const [editingChildAmount, setEditingChildAmount] = useState<string | null>(null)
  const [editingAmount, setEditingAmount] = useState(0)
  const [syncingTrials, setSyncingTrials] = useState(false)
  const childRef = useRef<HTMLDivElement>(null)

  // Campaign transfer
  const [transferSearch, setTransferSearch] = useState('')
  const [transferDropdownOpen, setTransferDropdownOpen] = useState(false)
  const [transferring, setTransferring] = useState(false)
  const [transferTarget, setTransferTarget] = useState<any>(null)
  const [showTransferConfirm, setShowTransferConfirm] = useState(false)
  const [transferToast, setTransferToast] = useState<{ success: boolean; message: string } | null>(null)
  const transferRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (id) loadData()
  }, [id])

  useEffect(() => {
    if (id) loadSlotData()
  }, [id])

  // Load all accounts for library sharing search
  useEffect(() => {
    const token = getAdminToken()
    if (!token) return
    axios.get(`${process.env.REACT_APP_API_URL}/admin/search-accounts`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    }).then(res => {
      setAllAccountsForSharing(res.data?.accounts || [])
    }).catch(() => {})
  }, [])

  // Close sharing/child dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sharingRef.current && !sharingRef.current.contains(e.target as Node)) {
        setSharingDropdownOpen(false)
      }
      if (childRef.current && !childRef.current.contains(e.target as Node)) {
        setChildDropdownOpen(false)
      }
      if (transferRef.current && !transferRef.current.contains(e.target as Node)) {
        setTransferDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Auto-dismiss transfer toast
  useEffect(() => {
    if (!transferToast) return
    const timer = setTimeout(() => setTransferToast(null), 3500)
    return () => clearTimeout(timer)
  }, [transferToast])

  const loadData = async () => {
    setLoading(true)
    try {
      const [res, meetingsRes, sharingRes, childRes] = await Promise.all([
        getAccountDetail(id!),
        getMeetings({ account: id, status: 'scheduled', sort: 'meeting_date', limit: 10 }).catch(() => null),
        getLibrarySharing(id!).catch(() => null),
        getChildAccounts(id!).catch(() => null),
      ])
      setAccount(res.data.account)
      setProfiles(res.data.profiles)
      setLogs(res.data.logs)
      setInvoices(res.data.invoices)
      setEditData(res.data.account)
      if (meetingsRes) {
        setAccountMeetings(meetingsRes.data.meetings)
      }
      if (sharingRes) {
        setSharedAccounts(sharingRes.data.shared_accounts)
      }
      if (childRes) {
        setChildAccounts(childRes.data.child_accounts)
        setBillingDay(childRes.data.billing_day || 1)
      }
    } catch (error) {
      console.error('Failed to load account:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadSlotData = async () => {
    if (!id) return
    setLoadingSlots(true)
    try {
      const [slotsRes, timelineRes, paymentsRes] = await Promise.all([
        getAccountSlots(id),
        getPackageTimeline(id, { limit: 20 }),
        getPlanPayments(id, { limit: 20 })
      ])
      setSlots(slotsRes.data)
      setPackageChanges(timelineRes.data.changes || [])
      setPlanPayments(paymentsRes.data.payments || [])
    } catch (error) {
      console.error('Failed to load slot data:', error)
    } finally {
      setLoadingSlots(false)
    }
  }

  const handleAddSlot = async () => {
    if (!account) return
    setAddingSlot(true)
    try {
      await addAccountSlot(account._id, {
        slot_type: addSlotType,
        is_free: addSlotFree,
        reason: addSlotReason || undefined
      })
      await loadSlotData()
      await loadData()
      setShowAddSlotModal(false)
      setAddSlotReason('')
    } catch (error: any) {
      console.error('Failed to add slot:', error)
      alert('Failed to add slot: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setAddingSlot(false)
    }
  }

  const handleSave = async () => {
    if (!account) return
    setSaving(true)
    try {
      await updateAccount(account._id, editData)
      await loadData()
      setEditMode(false)
    } catch (error) {
      console.error('Failed to update account:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleTransferCampaigns = async () => {
    if (!id || !account || !transferTarget) return
    setTransferring(true)
    try {
      const res = await transferCampaigns(id, transferTarget._id)
      setShowTransferConfirm(false)
      setTransferTarget(null)
      setTransferSearch('')
      setTransferDropdownOpen(false)
      setTransferToast({ success: true, message: `${res.data.transferredCount} קמפיינים הועברו בהצלחה` })
      await loadData()
    } catch (e: any) {
      setShowTransferConfirm(false)
      setTransferToast({ success: false, message: e.response?.data?.error || 'שגיאה בהעברת קמפיינים' })
    }
    setTransferring(false)
  }

  const handleToggleSharing = async (acc: any) => {
    if (!id) return
    const isShared = sharedAccounts.some(a => a._id === acc._id)
    if (isShared) {
      try {
        await removeLibrarySharing(id, acc._id)
        setSharedAccounts(prev => prev.filter(a => a._id !== acc._id))
      } catch (e) {
        alert('Failed to remove')
      }
    } else {
      setAddingSharing(acc._id)
      try {
        const res = await addLibrarySharing(id, acc._id)
        setSharedAccounts(prev => [...prev, res.data.account])
      } catch (e: any) {
        alert(e.response?.data?.error || 'Failed to add')
      }
      setAddingSharing(null)
    }
  }

  const handleToggleChild = async (acc: any) => {
    if (!id) return
    const isChild = childAccounts.some(a => a._id === acc._id)
    if (isChild) {
      if (!window.confirm(`Remove ${acc.email} from child accounts?`)) return
      try {
        await removeChildAccount(id, acc._id)
        setChildAccounts(prev => prev.filter(a => a._id !== acc._id))
      } catch (e) {
        alert('Failed to remove')
      }
    } else {
      setAddingChild(acc._id)
      try {
        const res = await addChildAccount(id, { target_account_id: acc._id, monthly_amount: 0 })
        setChildAccounts(prev => [...prev, res.data.account])
      } catch (e: any) {
        alert(e.response?.data?.error || 'Failed to add')
      }
      setAddingChild(null)
    }
  }

  const handleUpdateChildAmount = async (childId: string, amount: number) => {
    if (!id) return
    try {
      await updateChildAccount(id, childId, { monthly_amount: amount })
      setChildAccounts(prev => prev.map(c => c._id === childId ? { ...c, monthly_amount: amount } : c))
      setEditingChildAmount(null)
    } catch (e) {
      alert('Failed to update')
    }
  }

  const handleSyncChildTrials = async () => {
    if (!id) return
    if (!account?.trial_period_end) {
      alert('לחשבון ההורה אין תאריך סיום ניסיון לסנכרון')
      return
    }
    if (!window.confirm(`לסנכרן את תאריך סיום הניסיון של ${childAccounts.length} חשבונות הילדים לתאריך של ההורה (${moment(account.trial_period_end).format('DD/MM/YY')})?`)) return
    setSyncingTrials(true)
    try {
      const res = await syncChildTrials(id)
      // refresh children so the displayed dates update
      const childRes = await getChildAccounts(id)
      setChildAccounts(childRes.data.child_accounts)
      alert(`סונכרנו ${res.data.synced_count} חשבונות ילדים`)
    } catch (e: any) {
      alert(e.response?.data?.error || 'סנכרון נכשל')
    }
    setSyncingTrials(false)
  }

  const handleExtendTrial = async () => {
    if (!account) return
    if (!trialReason.trim()) {
      alert('יש להזין סיבה לשינוי')
      return
    }
    setSaving(true)
    try {
      const data: { days?: number; new_date?: string; reason: string } = { reason: trialReason }
      if (trialMode === 'date' && trialDate) {
        data.new_date = trialDate
      } else {
        data.days = trialDays
      }
      await extendTrial(account._id, data)
      await loadData()
      setShowTrialModal(false)
      setTrialReason('')
      setTrialDays(30)
      setTrialDate('')
    } catch (error: any) {
      console.error('Failed to extend trial:', error)
      alert('שגיאה בשינוי תקופת מנוי: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleSaveMonthlyAmount = async () => {
    if (!account) return
    const trimmed = monthlyDraft.trim()
    const parsed = trimmed === '' ? 0 : parseFloat(trimmed)
    if (isNaN(parsed) || parsed < 0) {
      alert('סכום לא תקין')
      return
    }
    if (parsed === (account.monthly_payment_amount || 0)) {
      setEditingMonthly(false)
      return
    }
    setSavingMonthly(true)
    try {
      await updateAccount(account._id, { monthly_payment_amount: parsed })
      await loadData()
      setEditingMonthly(false)
    } catch (error: any) {
      alert('שגיאה בעדכון סכום החיוב: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSavingMonthly(false)
    }
  }

  const handleCustomCharge = async () => {
    if (!account) return
    const amount = parseFloat(chargeAmount)
    if (!amount || amount <= 0) {
      setChargeResult({ ok: false, message: 'יש להזין סכום לחיוב' })
      return
    }
    if (!chargeReason.trim()) {
      setChargeResult({ ok: false, message: 'יש להזין סיבה לחיוב (תופיע בחשבונית)' })
      return
    }
    setCharging(true)
    setChargeResult(null)
    try {
      const { data } = await chargeCustomAmount(account._id, {
        amount,
        reason: chargeReason.trim(),
        installments: chargeInstallments,
      })
      if (data.success) {
        setChargeResult({ ok: true, message: `חויב ₪${data.amount_charged} בהצלחה. חשבונית נשלחה ללקוח. לא נוספו ימים למנוי.` })
        setChargeAmount('')
        setChargeReason('')
        setChargeInstallments(1)
        await loadData()
      } else {
        setChargeResult({ ok: false, message: data.error || 'החיוב נכשל' })
      }
    } catch (error: any) {
      setChargeResult({ ok: false, message: error?.response?.data?.error || error?.message || 'החיוב נכשל' })
    } finally {
      setCharging(false)
    }
  }

  const handleToggleGroupScheduling = async () => {
    if (!account) return
    setSaving(true)
    try {
      await toggleGroupScheduling(account._id, !account.group_scheduling_enabled)
      await loadData()
    } catch (error: any) {
      console.error('Failed to toggle group scheduling:', error)
      alert('שגיאה בעדכון תזמון קבוצות: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleXpostSpread = async () => {
    if (!account) return
    setSaving(true)
    try {
      await toggleXpostSpread(account._id, !account.xpost_spread_across_day)
      await loadData()
    } catch (error: any) {
      console.error('Failed to toggle xpost spread:', error)
      alert('שגיאה בעדכון פיזור פרסום: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleAnonPostLimited = async () => {
    if (!account) return
    setSaving(true)
    try {
      await toggleAnonPostLimited(account._id, !account.anon_post_when_limited)
      await loadData()
    } catch (error: any) {
      console.error('Failed to toggle anonymous posting:', error)
      alert('שגיאה בעדכון פרסום אנונימי: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleSeedGroups = async () => {
    if (!account) return
    const turningOff = !account.seed_group_disabled
    if (turningOff && !window.confirm(
      'לכבות לגמרי את פיצ\'ר קבוצות ה-seed ללקוח הזה?\n\n' +
      'לא תיווצר יותר אף קבוצה תחת הפרופילים שלו (גם לפרופילים שיתווספו בעתיד), ' +
      'וכל הפרופילים הקיימים ייצאו ממצב seed.'
    )) return
    setSaving(true)
    try {
      const res = await toggleSeedGroups(account._id, !turningOff)
      await loadData()
      if (turningOff && res?.data?.disarmed) {
        alert(`קבוצות seed כובו. ${res.data.disarmed} פרופילים יצאו ממצב seed.`)
      }
    } catch (error: any) {
      console.error('Failed to toggle seed groups:', error)
      alert('שגיאה בעדכון קבוצות seed: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleTogglePaypal = async () => {
    if (!account) return
    setSaving(true)
    try {
      await togglePaypal(account._id, !account.paypal_enabled)
      await loadData()
    } catch (error: any) {
      alert('שגיאה בעדכון PayPal: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleCancelPaypal = async () => {
    if (!account) return
    if (!window.confirm('לבטל את מנוי ה-PayPal של הלקוח? לא יבוצעו חיובים נוספים. התקופה ששולמה נשארת.')) return
    setSaving(true)
    try {
      await cancelPaypalSubscription(account._id)
      await loadData()
    } catch (error: any) {
      alert('שגיאה בביטול PayPal: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleApiAccess = async () => {
    if (!account) return
    setSaving(true)
    try {
      await toggleApiAccess(account._id, !account.api_access_enabled)
      await loadData()
    } catch (error: any) {
      alert('שגיאה בעדכון: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleMarketplaceCheckbox = async () => {
    if (!account) return
    setSaving(true)
    try {
      await toggleMarketplaceCheckbox(account._id, !account.marketplace_checkbox_enabled)
      await loadData()
    } catch (error: any) {
      alert('שגיאה בעדכון: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleStatsRealNumbers = async () => {
    if (!account) return
    setSaving(true)
    try {
      await toggleStatsRealNumbers(account._id, !account.stats_real_numbers)
      await loadData()
    } catch (error: any) {
      console.error('Failed to toggle stats real numbers:', error)
      alert('שגיאה בעדכון תצוגת הסטטיסטיקה: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleEmailVerified = async () => {
    if (!account) return
    setSaving(true)
    try {
      await toggleEmailVerified(account._id, !account.email_verified)
      await loadData()
    } catch (error: any) {
      console.error('Failed to toggle email verification:', error)
      alert('שגיאה בעדכון אימות אימייל: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleSchedule = async () => {
    if (!account) return
    setSaving(true)
    try {
      await toggleSchedule(account._id, !account.schedule_enabled)
      await loadData()
    } catch (error: any) {
      console.error('Failed to toggle schedule:', error)
      alert('שגיאה בעדכון לוח זמנים: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleSetPostFooterPosition = async (target: 'start' | 'end') => {
    if (!account) return
    const current = account.dynamic_options?.post_footer_position
      || (account.dynamic_options?.post_footer_enabled ? 'end' : null)
    const next = current === target ? null : target
    setSaving(true)
    try {
      await setPostFooterPosition(account._id, next)
      await loadData()
    } catch (error: any) {
      console.error('Failed to set post footer position:', error)
      alert('שגיאה בעדכון פוטר פוסט: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleForceLogout = async () => {
    if (!account) return
    if (!window.confirm('לנתק את המשתמש הזה מכל המכשירים? המשתמש יידרש להתחבר מחדש.')) return
    setSaving(true)
    try {
      const res = await forceLogoutUser(account._id)
      if (res.data.success) {
        alert(`המשתמש נותק. token_version: ${res.data.token_version}`)
        await loadData()
      }
    } catch (error: any) {
      console.error('Failed to force-logout user:', error)
      alert('שגיאה בניתוק המשתמש: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  // Manual welcome WhatsApp. A Google sign-up never gets one (no phone and no real
  // password at signup time), so this is the way to send it after the fact — and it
  // can be pressed as often as needed: the server re-uses the password it generated,
  // so a re-send repeats the same working message instead of invalidating the one
  // the customer already has. newPassword forces a fresh one.
  const handleSendWelcome = async (newPassword = false) => {
    if (!account) return
    const target = account.phone || '(אין טלפון)'
    if (!window.confirm(
      newPassword
        ? `לשלוח "ברוכים הבאים" אל ${target} עם סיסמה חדשה?\n\nהסיסמה הנוכחית של הלקוח תאופס.`
        : `לשלוח את הודעת "ברוכים הבאים" בוואטסאפ אל ${target}?\n\n` +
          'ניתן לשלוח שוב בכל עת — תישלח אותה סיסמה. סיסמה חדשה תיווצר רק אם אין אחת שמורה.'
    )) return
    setSaving(true)
    try {
      const res = await sendWelcomeMessage(account._id, newPassword)
      if (res.data.success) {
        alert(
          `ההודעה נשלחה אל ${res.data.phone}\n` +
          (res.data.reused ? `סיסמה (ללא שינוי): ${res.data.password}` : `סיסמה חדשה: ${res.data.password}`)
        )
        await loadData()
      }
    } catch (error: any) {
      console.error('Failed to send welcome message:', error)
      alert('שגיאה בשליחת ההודעה: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleAffiliate = async () => {
    if (!account) return
    const next = !(account as any).is_affiliate
    if (!window.confirm(next ? 'להפעיל מצב שותף (Affiliate) לחשבון זה?' : 'לכבות מצב שותף לחשבון זה?')) return
    setSaving(true)
    try {
      const res = await toggleAffiliate(account._id, next)
      if (res.data.success) {
        setAccount((prev: any) => prev ? { ...prev, is_affiliate: res.data.is_affiliate } : prev)
      }
    } catch (error: any) {
      console.error('Failed to toggle affiliate:', error)
      alert('שגיאה בעדכון מצב שותף: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleToggleBulkProfiles = async () => {
    if (!account) return
    const next = !(account as any).bulk_profiles_enabled
    setSaving(true)
    try {
      const res = await setBulkProfiles(account._id, { enabled: next })
      if (res.data.success) setAccount((prev: any) => prev ? { ...prev, bulk_profiles_enabled: res.data.bulk_profiles_enabled } : prev)
    } catch (error: any) {
      alert('שגיאה: ' + (error?.response?.data?.error || error?.message))
    } finally { setSaving(false) }
  }

  const handleSetMaxProfiles = async (max: number) => {
    if (!account) return
    try {
      const res = await setBulkProfiles(account._id, { max })
      if (res.data.success) setAccount((prev: any) => prev ? { ...prev, max_self_profiles: res.data.max_self_profiles } : prev)
    } catch (error: any) {
      alert('שגיאה: ' + (error?.response?.data?.error || error?.message))
    }
  }

  const handleLoginAsUser = async () => {
    if (!account) return
    try {
      const res = await loginAsUser(account._id)
      if (res.data.api_token) {
        // Store the token and open in new tab
        const authData = JSON.stringify({ api_token: res.data.api_token })
        // Open new window with the token
        const newWindow = window.open('about:blank', '_blank')
        if (newWindow) {
          newWindow.localStorage.setItem('kt-auth-react-v', authData)
          newWindow.location.href = restoreMode ? '/campaigns?restore=1' : '/campaigns'
        }
      }
    } catch (error: any) {
      console.error('Failed to login as user:', error)
      alert('שגיאה בכניסה כמשתמש: ' + (error?.response?.data?.error || error?.message || 'Unknown error'))
    }
  }

  const handleToggleActive = async () => {
    if (!account) return
    setSaving(true)
    try {
      await toggleAccountActivation(account._id, account.is_locked === true)
      await loadData()
    } catch (error) {
      console.error('Failed to toggle activation:', error)
    } finally {
      setSaving(false)
    }
  }

  // --- Full account disable ------------------------------------------------
  // Distinct from the נעל button above, which only flips is_locked. This one
  // also returns rented profiles to the incubator, frees the virtual numbers and
  // burns the desktop client key — none of which can be undone from the UI, so
  // it always previews the exact impact before running.
  const [disableImpact, setDisableImpact] = useState<DisableImpact | null>(null)
  const [disableLoading, setDisableLoading] = useState(false)
  const [disableRunning, setDisableRunning] = useState(false)
  const [disableConfirmText, setDisableConfirmText] = useState('')

  const openDisableDialog = async () => {
    if (!account) return
    setDisableConfirmText('')
    setDisableLoading(true)
    try {
      const { data } = await getAccountDisableImpact(account._id)
      setDisableImpact(data)
    } catch (error: any) {
      alert('טעינת פרטי ההשבתה נכשלה: ' + (error?.response?.data?.error || error?.message))
    } finally {
      setDisableLoading(false)
    }
  }

  const handleDisableAccount = async () => {
    if (!account) return
    setDisableRunning(true)
    try {
      const { data } = await disableAccount(account._id)
      setDisableImpact(null)
      await loadData()
      const parts = [
        `${data.profiles_moved} פרופילים הוחזרו למאגר`,
        `${data.virtual_numbers_released.length} מספרים וירטואליים שוחררו`,
      ]
      alert('החשבון הושבת.\n' + parts.join('\n'))
    } catch (error: any) {
      alert('השבתת החשבון נכשלה: ' + (error?.response?.data?.error || error?.message))
    } finally {
      setDisableRunning(false)
    }
  }

  const handleToggleProfile = async (profileId: string, enabled: boolean) => {
    if (!account) return
    try {
      await toggleProfileStatus(account._id, profileId, enabled)
      await loadData()
    } catch (error) {
      console.error('Failed to toggle profile:', error)
    }
  }

  const handleAddLog = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!account || (!newLogSubject && !newLogDescription)) return
    try {
      await addAccountLog(account._id, {
        subject: newLogSubject || 'הערת אדמין',
        description: newLogDescription
      })
      setNewLogSubject('')
      setNewLogDescription('')
      await loadData()
    } catch (error) {
      console.error('Failed to add log:', error)
    }
  }

  const handleDeleteLog = async (logId: string) => {
    if (!account) return
    if (!window.confirm('האם למחוק את ההערה?')) return
    try {
      await deleteAccountLog(account._id, logId)
      await loadData()
    } catch (error) {
      console.error('Failed to delete log:', error)
    }
  }

  const getAccountName = () => {
    if (!account) return ''
    return `${account.first_name || ''} ${account.last_name || ''}`.trim() || account.email
  }

  const getStatusStyle = (status: string) => {
    const styles: Record<string, { bg: string; color: string; gradient: string }> = {
      success: { bg: 'rgba(56, 239, 125, 0.1)', color: '#38ef7d', gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
      info: { bg: 'rgba(79, 172, 254, 0.1)', color: '#4facfe', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
      warning: { bg: 'rgba(255, 212, 59, 0.1)', color: '#ffd43b', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
      danger: { bg: 'rgba(255, 107, 107, 0.1)', color: '#ff6b6b', gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)' },
      secondary: { bg: 'rgba(134, 142, 150, 0.1)', color: '#868e96', gradient: 'linear-gradient(135deg, #868e96 0%, #6c7293 100%)' },
    }
    return styles[status] || styles.secondary
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: `3px solid ${colors.border}`,
          borderTopColor: '#667eea',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}></div>
        <div style={{ color: colors.textMuted, fontSize: '14px' }}>טוען פרטי חשבון...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  if (!account) {
    return (
      <div style={{
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '20px',
          background: 'rgba(255, 107, 107, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <i className="bi bi-person-x" style={{ fontSize: '32px', color: '#ff6b6b' }}></i>
        </div>
        <div style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>חשבון לא נמצא</div>
        <button
          onClick={() => navigate('/admin/accounts')}
          style={{
            padding: '10px 24px',
            borderRadius: '10px',
            border: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          חזרה לרשימה
        </button>
      </div>
    )
  }

  const statusColor = getAccountStatusColor(account)
  const statusStyle = getStatusStyle(statusColor)

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
        paddingBottom: '10px',
        borderBottom: `1px solid ${colors.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={() => navigate('/admin/accounts')}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: colors.bgSecondary,
              color: colors.textSecondary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="bi bi-arrow-right" style={{ fontSize: '14px' }}></i>
          </button>

          <div style={{
            width: '38px',
            height: '38px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '15px',
            color: '#fff',
          }}>
            {getAccountName().charAt(0).toUpperCase()}
          </div>

          <div>
            <h1 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: 700,
              color: colors.textPrimary,
            }}>
              {getAccountName()}
            </h1>
            <div style={{
              color: colors.textMuted,
              fontSize: '11px',
              fontFamily: 'monospace',
            }}>
              {account.email}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{
            padding: '5px 10px',
            borderRadius: '8px',
            fontSize: '11px',
            fontWeight: 600,
            background: statusStyle.bg,
            color: statusStyle.color,
            border: `1px solid ${statusStyle.color}30`,
          }}>
            {getAccountStatusLabel(account)}
          </span>
          {account.group_scheduling_enabled && (
            <span style={{
              padding: '5px 10px',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: 600,
              background: 'rgba(17, 153, 142, 0.1)',
              color: '#11998e',
              border: '1px solid rgba(17, 153, 142, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              <i className="bi bi-clock"></i> תזמון קבוצות
            </span>
          )}
          {account.schedule_enabled && (
            <span style={{
              padding: '5px 10px',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: 600,
              background: 'rgba(24, 119, 242, 0.1)',
              color: '#1877f2',
              border: '1px solid rgba(24, 119, 242, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              <i className="bi bi-calendar-week"></i> לוח זמנים
            </span>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '12px',
        flexWrap: 'wrap',
      }}>
        <button
          onClick={() => setShowTrialModal(true)}
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: 'none',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          <i className="bi bi-calendar-plus" style={{ fontSize: '12px' }}></i>
          תקופת מנוי
        </button>

        {/* One-off charge — only offered when there is a card to charge. */}
        <button
          onClick={() => { setChargeResult(null); setShowChargeModal(true) }}
          disabled={!account.has_payment_token}
          title={account.has_payment_token
            ? 'חיוב חד-פעמי בכרטיס השמור, ללא הוספת ימים למנוי'
            : 'אין כרטיס אשראי שמור בחשבון'}
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: 'none',
            background: account.has_payment_token
              ? 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)'
              : colors.bgTertiary,
            color: account.has_payment_token ? '#3d2c00' : colors.textMuted,
            cursor: account.has_payment_token ? 'pointer' : 'not-allowed',
            fontWeight: 600,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            opacity: account.has_payment_token ? 1 : 0.6,
          }}
        >
          <i className="bi bi-credit-card" style={{ fontSize: '12px' }}></i>
          חיוב חד-פעמי
          {account.has_payment_token && account.payment_card_last4 ? ` ••${account.payment_card_last4}` : ''}
        </button>

        {/* Group Scheduling Toggle */}
        <div
          onClick={() => !saving && handleToggleGroupScheduling()}
          style={{
            padding: '6px 10px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: colors.bgSecondary,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <i className="bi bi-clock" style={{ color: colors.textSecondary, fontSize: '12px' }}></i>
          <span style={{ color: colors.textPrimary, fontSize: '11px', fontWeight: 500 }}>תזמון קבוצות</span>
          <div style={{
            width: '28px',
            height: '16px',
            borderRadius: '8px',
            background: account.group_scheduling_enabled
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'rgba(108, 114, 147, 0.3)',
            position: 'relative',
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '6px',
              background: '#fff',
              position: 'absolute',
              top: '2px',
              right: account.group_scheduling_enabled ? '2px' : '14px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>

        {/* Spread cross-posts across the day (admin-only): defers later-hour xpost
            copies so posting lands morning/afternoon/evening, not one morning burst. */}
        <div
          onClick={() => !saving && handleToggleXpostSpread()}
          style={{
            padding: '6px 10px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: colors.bgSecondary,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: saving ? 0.6 : 1,
          }}
          title="פיזור פרסום ה-xpost על פני היום (בוקר/צהריים/ערב) במקום ריכוז בבוקר"
        >
          <i className="bi bi-clock-history" style={{ color: colors.textSecondary, fontSize: '12px' }}></i>
          <span style={{ color: colors.textPrimary, fontSize: '11px', fontWeight: 500 }}>פיזור פרסום על היום</span>
          <div style={{
            width: '28px',
            height: '16px',
            borderRadius: '8px',
            background: account.xpost_spread_across_day
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'rgba(108, 114, 147, 0.3)',
            position: 'relative',
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '6px',
              background: '#fff',
              position: 'absolute',
              top: '2px',
              right: account.xpost_spread_across_day ? '2px' : '14px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>

        {/* Seed groups (admin-only): off is a HARD opt-out — the fleet never creates
            a group under this client's FB identity, now or for profiles added later,
            and every existing profile is taken out of seed mode. */}
        <div
          onClick={() => !saving && handleToggleSeedGroups()}
          style={{
            padding: '6px 10px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: colors.bgSecondary,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: saving ? 0.6 : 1,
          }}
          title="יצירת קבוצות seed לפרסום צולב. כיבוי = הפיצ'ר לא יפעל ללקוח הזה לעולם, ולא תיווצר אף קבוצה תחת הפרופילים שלו"
        >
          <i className="bi bi-people" style={{ color: colors.textSecondary, fontSize: '12px' }}></i>
          <span style={{ color: colors.textPrimary, fontSize: '11px', fontWeight: 500 }}>קבוצות seed</span>
          <div style={{
            width: '28px',
            height: '16px',
            borderRadius: '8px',
            background: !account.seed_group_disabled
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'rgba(108, 114, 147, 0.3)',
            position: 'relative',
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '6px',
              background: '#fff',
              position: 'absolute',
              top: '2px',
              right: !account.seed_group_disabled ? '2px' : '14px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>

        {/* PayPal subscriptions (admin-only, default off): shows the PayPal
            subscribe button to this customer. Turning it off does not cancel a
            live subscription; the cancel chip next to it does. */}
        <div
          onClick={() => !saving && handleTogglePaypal()}
          style={{
            padding: '6px 10px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: colors.bgSecondary,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: saving ? 0.6 : 1,
          }}
          title="מאפשר ללקוח לשלם במנוי PayPal (חיוב אוטומטי חודשי/שנתי)"
        >
          <i className="bi bi-paypal" style={{ color: colors.textSecondary, fontSize: '12px' }}></i>
          <span style={{ color: colors.textPrimary, fontSize: '11px', fontWeight: 500 }}>PayPal</span>
          <div style={{
            width: '28px',
            height: '16px',
            borderRadius: '8px',
            background: account.paypal_enabled
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'rgba(108, 114, 147, 0.3)',
            position: 'relative',
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '6px',
              background: '#fff',
              position: 'absolute',
              top: '2px',
              right: account.paypal_enabled ? '2px' : '14px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>
        {account.paypal_subscription?.id && (
          <div
            style={{
              padding: '6px 10px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              background: colors.bgSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '11px',
              color: colors.textPrimary,
            }}
            title={`מנוי ${account.paypal_subscription.id}${account.paypal_subscription.payer_email ? ' · ' + account.paypal_subscription.payer_email : ''}`}
          >
            <span>
              PayPal: {account.paypal_subscription.status}
              {account.paypal_subscription.price ? ` · ₪${account.paypal_subscription.price}/${account.paypal_subscription.period === 'yearly' ? 'שנה' : 'חודש'}` : ''}
              {account.paypal_subscription.next_billing_at ? ` · הבא ${new Date(account.paypal_subscription.next_billing_at).toLocaleDateString('he-IL')}` : ''}
            </span>
            {['ACTIVE', 'SUSPENDED', 'APPROVAL_PENDING'].includes(account.paypal_subscription.status || '') && (
              <button
                onClick={() => !saving && handleCancelPaypal()}
                disabled={saving}
                style={{ background: 'none', border: 'none', color: '#f1416c', cursor: 'pointer', fontSize: '11px', padding: 0 }}
              >
                ביטול
              </button>
            )}
          </div>
        )}

        {/* Anonymous posting while limited (admin-only, default off): a profile FB
            has limited skips xpost/seed groups and posts to each group anonymously. */}
        <div
          onClick={() => !saving && handleToggleAnonPostLimited()}
          style={{
            padding: '6px 10px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: colors.bgSecondary,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: saving ? 0.6 : 1,
          }}
          title="כשפרופיל מוגבל בפייסבוק: במקום xpost, מפרסם בכל קבוצה כפוסט אנונימי (ברוב הקבוצות דורש אישור מנהל). קבוצה שלא מאפשרת פוסט אנונימי — הסלוט ייכשל"
        >
          <i className="bi bi-incognito" style={{ color: colors.textSecondary, fontSize: '12px' }}></i>
          <span style={{ color: colors.textPrimary, fontSize: '11px', fontWeight: 500 }}>פרסום אנונימי כשמוגבל</span>
          <div style={{
            width: '28px',
            height: '16px',
            borderRadius: '8px',
            background: account.anon_post_when_limited
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'rgba(108, 114, 147, 0.3)',
            position: 'relative',
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '6px',
              background: '#fff',
              position: 'absolute',
              top: '2px',
              right: account.anon_post_when_limited ? '2px' : '14px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>

        {/* Stats view (admin-only): on = the client's /stats shows REAL numbers
            (actual publish times + posts actually published today); off = the
            gradual view where posts appear on their scheduled hour. */}
        <div
          onClick={() => !saving && handleToggleStatsRealNumbers()}
          style={{
            padding: '6px 10px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: colors.bgSecondary,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: saving ? 0.6 : 1,
          }}
          title="סטטיסטיקה אמיתית ללקוח: כשמופעל, עמוד הסטטיסטיקה מציג את מספרי הפרסום האמיתיים (כולל מה שפורסם מוקדם). כבוי = תצוגה הדרגתית לפי שעת התזמון"
        >
          <i className="bi bi-bar-chart" style={{ color: colors.textSecondary, fontSize: '12px' }}></i>
          <span style={{ color: colors.textPrimary, fontSize: '11px', fontWeight: 500 }}>סטטיסטיקה אמיתית</span>
          <div style={{
            width: '28px',
            height: '16px',
            borderRadius: '8px',
            background: account.stats_real_numbers
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'rgba(108, 114, 147, 0.3)',
            position: 'relative',
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '6px',
              background: '#fff',
              position: 'absolute',
              top: '2px',
              right: account.stats_real_numbers ? '2px' : '14px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>

        {/* Post editor "🛒 publish to Marketplace too" checkbox — automatic for real-estate /
            cars accounts; this switch shows it to anyone else. */}
        {(() => {
          const auto = ['real_estate', 'cars'].includes(String(account.library_occupation || ''))
          const on = auto || !!account.marketplace_checkbox_enabled
          return (
            <div
              onClick={() => !saving && !auto && handleToggleMarketplaceCheckbox()}
              style={{
                padding: '6px 10px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.bgSecondary,
                cursor: saving || auto ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: saving ? 0.6 : 1,
              }}
              title={auto ? 'מוצג אוטומטית — הלקוח בתחום נדל״ן / רכב' : 'הצגת התיבה "פרסום גם במרקטפלייס" ביצירת פוסט ללקוח הזה'}
            >
              <i className="bi bi-shop" style={{ color: colors.textSecondary, fontSize: '12px' }}></i>
              <span style={{ color: colors.textPrimary, fontSize: '11px', fontWeight: 500 }}>
                מרקטפלייס בפוסט{auto ? ' (אוטומטי)' : ''}
              </span>
              <div style={{
                width: '28px', height: '16px', borderRadius: '8px', position: 'relative',
                background: on ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : 'rgba(108, 114, 147, 0.3)',
              }}>
                <div style={{
                  width: '12px', height: '12px', borderRadius: '6px', background: '#fff',
                  position: 'absolute', top: '2px', right: on ? '2px' : '14px', transition: 'all 0.2s ease',
                }}></div>
              </div>
            </div>
          )
        })()}

        {/* Customer API: keys + docs page in the customer's sidebar (routes/customer-api.js). */}
        <div
          onClick={() => !saving && handleToggleApiAccess()}
          style={{
            padding: '6px 10px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.bgSecondary,
            cursor: saving ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: saving ? 0.6 : 1,
          }}
          title='גישת API ללקוח: ניהול קמפיינים ופוסטים עם מפתח משלו (עמוד "API" בתפריט שלו)'
        >
          <i className="bi bi-code-slash" style={{ color: colors.textSecondary, fontSize: '12px' }}></i>
          <span style={{ color: colors.textPrimary, fontSize: '11px', fontWeight: 500 }}>גישת API</span>
          <div style={{
            width: '28px', height: '16px', borderRadius: '8px', position: 'relative',
            background: account.api_access_enabled ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : 'rgba(108, 114, 147, 0.3)',
          }}>
            <div style={{
              width: '12px', height: '12px', borderRadius: '6px', background: '#fff',
              position: 'absolute', top: '2px', right: account.api_access_enabled ? '2px' : '14px', transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>

        {/* Image Watermark: per-client opt-in + position/size override + live preview.
            Global appearance (logo, text, colour, opacity) is set in admin settings. */}
        <div style={{ flexBasis: '100%' }}>
          <WatermarkOverridePanel accountId={account._id} />
        </div>

        {/* Feed-mode timeline deletes: failed ones listed + re-queueable. Hidden
            when the client never posted through feed mode. */}
        <div style={{ flexBasis: '100%' }}>
          <FeedTrashPanel accountId={account._id} />
        </div>

        {/* Post variations (admin-only): on/off, count, contact style, generate for all posts. */}
        <div style={{ flexBasis: '100%' }}>
          <PostVariationsSettingsPanel accountId={account._id} />
        </div>

        {/* Email Verified Toggle */}
        <div
          onClick={() => !saving && handleToggleEmailVerified()}
          style={{
            padding: '6px 10px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: colors.bgSecondary,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <i className="bi bi-envelope-check" style={{ color: colors.textSecondary, fontSize: '12px' }}></i>
          <span style={{ color: colors.textPrimary, fontSize: '11px', fontWeight: 500 }}>אימות אימייל</span>
          <div style={{
            width: '28px',
            height: '16px',
            borderRadius: '8px',
            background: account.email_verified
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : 'rgba(108, 114, 147, 0.3)',
            position: 'relative',
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '6px',
              background: '#fff',
              position: 'absolute',
              top: '2px',
              right: account.email_verified ? '2px' : '14px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>

        {/* Schedule Toggle */}
        <div
          onClick={() => !saving && handleToggleSchedule()}
          style={{
            padding: '6px 10px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: colors.bgSecondary,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <i className="bi bi-calendar-week" style={{ color: colors.textSecondary, fontSize: '12px' }}></i>
          <span style={{ color: colors.textPrimary, fontSize: '11px', fontWeight: 500 }}>לוח זמנים</span>
          <div style={{
            width: '28px',
            height: '16px',
            borderRadius: '8px',
            background: account.schedule_enabled
              ? 'linear-gradient(135deg, #1877f2 0%, #42a5f5 100%)'
              : 'rgba(108, 114, 147, 0.3)',
            position: 'relative',
          }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '6px',
              background: '#fff',
              position: 'absolute',
              top: '2px',
              right: account.schedule_enabled ? '2px' : '14px',
              transition: 'all 0.2s ease',
            }}></div>
          </div>
        </div>

        {/* Promo Text Position — end / start (mutually exclusive) */}
        {(() => {
          const promoPos: 'start' | 'end' | null =
            (account.dynamic_options?.post_footer_position as any)
            || (account.dynamic_options?.post_footer_enabled ? 'end' : null)
          const renderPill = (target: 'start' | 'end', label: string) => {
            const on = promoPos === target
            return (
              <div
                key={target}
                onClick={() => !saving && handleSetPostFooterPosition(target)}
                style={{
                  padding: '6px 10px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  background: colors.bgSecondary,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                <i className="bi bi-card-text" style={{ color: colors.textSecondary, fontSize: '12px' }}></i>
                <span style={{ color: colors.textPrimary, fontSize: '11px', fontWeight: 500 }}>{label}</span>
                <div style={{
                  width: '28px',
                  height: '16px',
                  borderRadius: '8px',
                  background: on
                    ? 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)'
                    : 'rgba(108, 114, 147, 0.3)',
                  position: 'relative',
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '6px',
                    background: '#fff',
                    position: 'absolute',
                    top: '2px',
                    right: on ? '2px' : '14px',
                    transition: 'all 0.2s ease',
                  }}></div>
                </div>
              </div>
            )
          }
          return <>{renderPill('end', 'טקסט פרסומי בסוף פוסט')}{renderPill('start', 'טקסט פרסומי בתחילת פוסט')}</>
        })()}

        <button
          onClick={handleToggleActive}
          disabled={saving}
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: 'none',
            background: account.is_locked
              ? 'rgba(56, 239, 125, 0.15)'
              : 'rgba(255, 212, 59, 0.15)',
            color: account.is_locked ? '#38ef7d' : '#ffd43b',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <i className={`bi ${account.is_locked ? 'bi-unlock' : 'bi-lock'}`} style={{ fontSize: '12px' }}></i>
          {account.is_locked ? 'הפעל' : 'נעל'}
        </button>

        <button
          onClick={openDisableDialog}
          disabled={disableLoading}
          title="מחזיר פרופילים למאגר, משחרר מספרים וירטואליים ומנתק את הקליינט"
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: '1px solid rgba(246, 78, 96, 0.35)',
            background: 'rgba(246, 78, 96, 0.15)',
            color: '#f64e60',
            cursor: disableLoading ? 'wait' : 'pointer',
            fontWeight: 600,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            opacity: disableLoading ? 0.6 : 1,
          }}
        >
          <i className='bi bi-x-octagon' style={{ fontSize: '12px' }}></i>
          {disableLoading ? 'טוען…' : 'השבת חשבון'}
        </button>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '8px',
            background: restoreMode ? 'rgba(255, 107, 107, 0.15)' : 'rgba(160, 174, 192, 0.1)',
            border: `1px solid ${restoreMode ? 'rgba(255, 107, 107, 0.3)' : colors.border}`,
            cursor: 'pointer',
            fontSize: '11px',
            fontWeight: 600,
            color: restoreMode ? '#ff6b6b' : colors.textSecondary,
          }}
        >
          <input
            type="checkbox"
            checked={restoreMode}
            onChange={(e) => setRestoreMode(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <i className="bi bi-arrow-counterclockwise" style={{ fontSize: '12px' }}></i>
          שחזור
        </label>

        <button
          onClick={handleLoginAsUser}
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: 'none',
            background: restoreMode
              ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)'
              : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          <i className={`bi ${restoreMode ? 'bi-arrow-counterclockwise' : 'bi-box-arrow-in-right'}`} style={{ fontSize: '12px' }}></i>
          {restoreMode ? 'כניסה לשחזור' : 'כניסה'}
        </button>

        <button
          onClick={handleForceLogout}
          disabled={saving}
          title="ניתוק כל הסשנים הפעילים של המשתמש"
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: `1px solid rgba(255, 107, 107, 0.4)`,
            background: 'rgba(255, 107, 107, 0.12)',
            color: '#ff6b6b',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <i className="bi bi-box-arrow-right" style={{ fontSize: '12px' }}></i>
          ניתוק סשנים
        </button>

        {/* Arrow function, not a bare handler reference: React would hand the click
            event through as the newPassword argument and force a reset every time. */}
        <button
          onClick={() => handleSendWelcome(false)}
          disabled={saving}
          title="שליחת הודעת ברוכים הבאים בוואטסאפ. אפשר לשלוח שוב בכל עת — תישלח אותה סיסמה"
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: `1px solid rgba(37, 211, 102, 0.4)`,
            background: 'rgba(37, 211, 102, 0.12)',
            color: '#25d366',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <i className="bi bi-whatsapp" style={{ fontSize: '12px' }}></i>
          שליחת ברוכים הבאים
        </button>

        <button
          onClick={() => handleSendWelcome(true)}
          disabled={saving}
          title="שליחה עם סיסמה חדשה (מאפסת את הסיסמה הנוכחית של הלקוח)"
          style={{
            padding: '6px 10px',
            borderRadius: '8px',
            border: `1px solid rgba(37, 211, 102, 0.4)`,
            background: 'transparent',
            color: '#25d366',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <i className="bi bi-key" style={{ fontSize: '12px' }}></i>
          סיסמה חדשה
        </button>

        <button
          onClick={handleToggleAffiliate}
          disabled={saving}
          title="הפעלה/כיבוי מצב שותף (Affiliate) לחשבון זה"
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: (account as any)?.is_affiliate ? 'none' : `1px solid rgba(124, 77, 255, 0.4)`,
            background: (account as any)?.is_affiliate
              ? 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)'
              : 'rgba(124, 77, 255, 0.12)',
            color: (account as any)?.is_affiliate ? '#fff' : '#7c4dff',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <i className="bi bi-people" style={{ fontSize: '12px' }}></i>
          {(account as any)?.is_affiliate ? 'שותף פעיל ✓' : 'הפעל מצב שותף'}
        </button>

        <button
          onClick={handleToggleBulkProfiles}
          disabled={saving}
          title="הפעלה/כיבוי ניהול פרופילים בכמות (חיבור פרופילים) ללקוח"
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: (account as any)?.bulk_profiles_enabled ? 'none' : `1px solid rgba(54, 153, 255, 0.4)`,
            background: (account as any)?.bulk_profiles_enabled
              ? 'linear-gradient(135deg, #3699ff 0%, #66b3ff 100%)'
              : 'rgba(54, 153, 255, 0.12)',
            color: (account as any)?.bulk_profiles_enabled ? '#fff' : '#3699ff',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          <i className="bi bi-collection" style={{ fontSize: '12px' }}></i>
          {(account as any)?.bulk_profiles_enabled ? 'פרופילים בכמות ✓' : 'הפעל פרופילים בכמות'}
        </button>

        {(account as any)?.bulk_profiles_enabled && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#5e6278' }}>
            <span>מקס׳ פרופילים חופשי:</span>
            <input
              type="number"
              min={0}
              defaultValue={(account as any)?.max_self_profiles || 0}
              onBlur={(e) => handleSetMaxProfiles(parseInt(e.target.value) || 0)}
              style={{ width: '64px', padding: '4px 6px', borderRadius: '6px', border: '1px solid #e4e6ef' }}
            />
          </div>
        )}

        <a
          href={`https://s1.fbzipper.com/admin/customer/${id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: '6px 12px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            background: 'rgba(160, 174, 192, 0.1)',
            color: colors.textSecondary,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            textDecoration: 'none',
          }}
        >
          <i className="bi bi-box-arrow-up-right" style={{ fontSize: '12px' }}></i>
          Old CRM
        </a>
      </div>

      {/* Upcoming Meetings Banner */}
      {accountMeetings.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 168, 0, 0.1) 0%, rgba(255, 212, 59, 0.1) 100%)',
          border: '1px solid rgba(255, 168, 0, 0.3)',
          borderRadius: '12px',
          padding: '12px 16px',
          marginBottom: '4px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: accountMeetings.length > 1 ? '8px' : '0',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="bi bi-calendar-event-fill" style={{ fontSize: '18px', color: '#ffa800' }}></i>
              <span style={{ fontWeight: 700, fontSize: '14px', color: '#e8590c' }}>
                {accountMeetings.length === 1 ? 'פגישה קרובה' : `${accountMeetings.length} פגישות קרובות`}
              </span>
            </div>
          </div>
          {accountMeetings.map(meeting => {
            const meetingDate = moment(meeting.meeting_date)
            const isToday = meetingDate.isSame(moment(), 'day')
            const isTomorrow = meetingDate.isSame(moment().add(1, 'day'), 'day')
            const dayLabel = isToday ? 'היום' : isTomorrow ? 'מחר' : meetingDate.format('dd DD/MM')
            const assignedName = typeof meeting.assigned_to === 'object' && meeting.assigned_to
              ? (meeting.assigned_to as any).username || ''
              : ''

            return (
              <div
                key={meeting._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: isToday ? 'rgba(255, 168, 0, 0.12)' : 'rgba(255, 255, 255, 0.6)',
                  borderRadius: '8px',
                  marginBottom: '4px',
                  borderRight: isToday ? '3px solid #ffa800' : '3px solid transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ textAlign: 'center', minWidth: '50px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: isToday ? '#e8590c' : colors.textSecondary }}>
                      {dayLabel}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: isToday ? '#e8590c' : colors.textPrimary }}>
                      {meetingDate.format('HH:mm')}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: colors.textPrimary }}>
                      {meeting.title}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.textMuted, display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{
                        background: 'rgba(255, 168, 0, 0.15)',
                        color: '#e8590c',
                        padding: '1px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 500,
                      }}>
                        {MEETING_TYPE_LABELS[meeting.meeting_type] || meeting.meeting_type}
                      </span>
                      {assignedName && <span>{assignedName}</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {reschedulingMeetingId === meeting._id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input
                        type="datetime-local"
                        value={rescheduleDate}
                        onChange={(e) => setRescheduleDate(e.target.value)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          border: `1px solid ${colors.border}`,
                          fontSize: '12px',
                          outline: 'none',
                        }}
                      />
                      <button
                        onClick={async () => {
                          if (!rescheduleDate) return
                          try {
                            await rescheduleMeeting(meeting._id, rescheduleDate)
                            setReschedulingMeetingId(null)
                            setRescheduleDate('')
                            await loadData()
                          } catch (err) {
                            console.error('Reschedule failed:', err)
                            alert('שגיאה בתזמון מחדש')
                          }
                        }}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: 'none',
                          background: '#ffa800',
                          color: '#fff',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        שמור
                      </button>
                      <button
                        onClick={() => { setReschedulingMeetingId(null); setRescheduleDate('') }}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          border: `1px solid ${colors.border}`,
                          background: colors.bgSecondary,
                          color: colors.textMuted,
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        ביטול
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setReschedulingMeetingId(meeting._id)
                        setRescheduleDate(meetingDate.format('YYYY-MM-DDTHH:mm'))
                      }}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        border: `1px solid ${colors.border}`,
                        background: colors.bgSecondary,
                        color: colors.textSecondary,
                        fontSize: '11px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <i className="bi bi-arrow-repeat"></i>
                      תזמון מחדש
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Content Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px',
      }}>
        {/* Basic Info Card */}
        <div style={{
          background: colors.bgSecondary,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          padding: '12px',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '13px',
              fontWeight: 700,
              color: colors.textPrimary,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '6px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <i className="bi bi-person" style={{ fontSize: '11px', color: '#fff' }}></i>
              </div>
              פרטי חשבון
            </h3>
            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '10px',
                  border: `1px solid ${colors.border}`,
                  background: colors.bgTertiary,
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease',
                }}
              >
                <i className="bi bi-pencil"></i> עריכה
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setEditMode(false)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '10px',
                    border: `1px solid ${colors.border}`,
                    background: colors.bgTertiary,
                    color: colors.textSecondary,
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  ביטול
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '10px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  {saving ? 'שומר...' : 'שמור'}
                </button>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gap: '6px' }}>
            {[
              { label: 'שם פרטי', key: 'first_name', type: 'text' },
              { label: 'שם משפחה', key: 'last_name', type: 'text' },
              { label: 'אימייל', key: 'email', type: 'email' },
              { label: 'טלפון', key: 'phone', type: 'tel' },
              { label: 'חברה', key: 'company_name', type: 'text' },
            ].map((field) => (
              <div key={field.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={{
                  fontSize: '10px',
                  color: colors.textMuted,
                  fontWeight: 500,
                  minWidth: '60px',
                }}>
                  {field.label}
                </label>
                {editMode ? (
                  <input
                    type={field.type}
                    value={(editData as any)[field.key] || ''}
                    onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                    style={{
                      flex: 1,
                      padding: '5px 8px',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`,
                      background: colors.bgTertiary,
                      color: colors.textPrimary,
                      fontSize: '11px',
                      outline: 'none',
                    }}
                  />
                ) : (
                  <div style={{
                    fontWeight: 500,
                    color: colors.textPrimary,
                    fontSize: '11px',
                  }}>
                    {(account as any)[field.key] || '-'}
                  </div>
                )}
              </div>
            ))}

            {/* Account Type */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{
                fontSize: '10px',
                color: colors.textMuted,
                fontWeight: 500,
                minWidth: '60px',
              }}>
                סוג חשבון
              </label>
              {editMode ? (
                <select
                  value={(editData as any).account_type || ''}
                  onChange={(e) => setEditData({ ...editData, account_type: e.target.value } as any)}
                  style={{
                    flex: 1,
                    padding: '5px 8px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    background: colors.bgTertiary,
                    color: colors.textPrimary,
                    fontSize: '11px',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">רגיל</option>
                  <option value="demo_free">דמו חינם (ללא תשלום)</option>
                  <option value="paid">משלם</option>
                  <option value="vip">VIP</option>
                </select>
              ) : (
                <div style={{
                  fontWeight: 500,
                  color: (account as any).account_type === 'demo_free' ? '#f59e0b' : colors.textPrimary,
                  fontSize: '11px',
                }}>
                  {(account as any).account_type === 'demo_free' ? 'דמו חינם' : (account as any).account_type === 'paid' ? 'משלם' : (account as any).account_type === 'vip' ? 'VIP' : 'רגיל'}
                </div>
              )}
            </div>

            {/* Admin Notes */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <label style={{
                fontSize: '10px',
                color: colors.textMuted,
                fontWeight: 500,
                minWidth: '60px',
                paddingTop: '5px',
              }}>
                הערת מנהל
              </label>
              {editMode ? (
                <textarea
                  value={(editData as any).admin_notes || ''}
                  onChange={(e) => setEditData({ ...editData, admin_notes: e.target.value } as any)}
                  rows={2}
                  style={{
                    flex: 1,
                    padding: '5px 8px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    background: colors.bgTertiary,
                    color: colors.textPrimary,
                    fontSize: '11px',
                    outline: 'none',
                    resize: 'vertical',
                  }}
                />
              ) : (
                <div style={{
                  fontWeight: 500,
                  color: (account as any).admin_notes ? colors.textPrimary : colors.textMuted,
                  fontSize: '11px',
                  maxWidth: '200px',
                  wordBreak: 'break-word',
                }}>
                  {(account as any).admin_notes || '-'}
                </div>
              )}
            </div>

            {/* Upcoming Meeting */}
            {(account as any).upcoming_meeting && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={{
                  fontSize: '10px',
                  color: colors.textMuted,
                  fontWeight: 500,
                  minWidth: '60px',
                }}>
                  פגישה קרובה
                </label>
                <div style={{
                  fontWeight: 600,
                  color: '#667eea',
                  fontSize: '11px',
                  background: 'rgba(102,126,234,0.1)',
                  padding: '3px 8px',
                  borderRadius: '6px',
                }}>
                  {moment((account as any).upcoming_meeting.meeting_date).format('DD/MM/YYYY HH:mm')}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{
                fontSize: '10px',
                color: colors.textMuted,
                fontWeight: 500,
              }}>
                הצטרפות
              </label>
              <div style={{
                fontWeight: 500,
                color: colors.textPrimary,
                fontSize: '11px',
              }}>
                {moment(account.created_at).format('DD/MM/YYYY')}
              </div>
            </div>
          </div>
        </div>

        {/* Billing Info Card */}
        <div style={{
          background: colors.bgSecondary,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          padding: '12px',
        }}>
          <h3 style={{
            margin: '0 0 10px 0',
            fontSize: '13px',
            fontWeight: 700,
            color: colors.textPrimary,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <i className="bi bi-credit-card" style={{ fontSize: '11px', color: '#fff' }}></i>
            </div>
            חיוב ותשלום
          </h3>

          <div style={{ display: 'grid', gap: '6px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 10px',
              background: 'rgba(56, 239, 125, 0.08)',
              borderRadius: '8px',
              border: '1px solid rgba(56, 239, 125, 0.2)',
            }}>
              <span style={{ color: colors.textSecondary, fontSize: '10px' }}>תשלום חודשי</span>
              {editingMonthly ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type='number'
                    min={0}
                    step='0.01'
                    autoFocus
                    value={monthlyDraft}
                    disabled={savingMonthly}
                    onChange={(e) => setMonthlyDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveMonthlyAmount()
                      if (e.key === 'Escape') setEditingMonthly(false)
                    }}
                    style={{
                      width: '80px',
                      padding: '4px 6px',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`,
                      background: colors.bgTertiary,
                      color: colors.textPrimary,
                      fontSize: '14px',
                      fontWeight: 700,
                      textAlign: 'center',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={handleSaveMonthlyAmount}
                    disabled={savingMonthly}
                    title='שמור'
                    style={{
                      border: 'none',
                      borderRadius: '6px',
                      background: '#11998e',
                      color: '#fff',
                      padding: '4px 7px',
                      fontSize: '11px',
                      cursor: savingMonthly ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <i className='bi bi-check-lg'></i>
                  </button>
                  <button
                    onClick={() => setEditingMonthly(false)}
                    disabled={savingMonthly}
                    title='ביטול'
                    style={{
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      background: colors.bgTertiary,
                      color: colors.textSecondary,
                      padding: '4px 7px',
                      fontSize: '11px',
                      cursor: savingMonthly ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <i className='bi bi-x-lg'></i>
                  </button>
                </span>
              ) : (
                <span
                  onClick={() => {
                    setMonthlyDraft(account.monthly_payment_amount ? String(account.monthly_payment_amount) : '')
                    setEditingMonthly(true)
                  }}
                  title='לחצו לעריכה — השינוי יתועד בהערות ולוג'
                  style={{
                    fontWeight: 700,
                    fontSize: '16px',
                    color: '#11998e',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  {account.monthly_payment_amount ? `₪${account.monthly_payment_amount}` : '—'}
                  <i className='bi bi-pencil' style={{ fontSize: '10px', color: colors.textMuted }}></i>
                </span>
              )}
            </div>

            {[
              { label: 'כולל מע"מ', value: account.monthly_payment_include_vat ? 'כן' : 'לא' },
              { label: 'מניעת חיוב', value: account.transaction_denial ? 'כן' : 'לא', danger: account.transaction_denial },
              { label: 'סיום מנוי', value: account.trial_period_end ? moment(account.trial_period_end).format('DD/MM/YYYY') : '—' },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '4px 0',
                borderBottom: `1px solid ${colors.borderLight}`,
              }}>
                <span style={{ color: colors.textSecondary, fontSize: '10px' }}>{item.label}</span>
                <span style={{
                  color: item.danger ? '#ff6b6b' : colors.textPrimary,
                  fontWeight: 500,
                  fontSize: '11px',
                }}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>

          {invoices.length > 0 && (
            <>
              <div style={{
                margin: '8px 0 6px',
                paddingTop: '8px',
                borderTop: `1px solid ${colors.borderLight}`,
              }}>
                <h4 style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: colors.textPrimary,
                  marginBottom: '6px',
                }}>
                  חשבוניות אחרונות
                </h4>
              </div>
              <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                {invoices.slice(0, 3).map(invoice => (
                  <div
                    key={invoice._id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '4px 6px',
                      marginBottom: '2px',
                      background: colors.bgTertiary,
                      borderRadius: '4px',
                    }}
                  >
                    <span style={{ color: colors.textMuted, fontSize: '10px' }}>
                      {moment(invoice.created_at).format('DD/MM/YY')}
                    </span>
                    <span style={{ color: colors.textPrimary, fontWeight: 600, fontSize: '10px' }}>
                      ₪{invoice.amount}
                    </span>
                    <span style={{
                      padding: '1px 5px',
                      borderRadius: '3px',
                      fontSize: '9px',
                      fontWeight: 600,
                      background: invoice.status === 'completed' ? 'rgba(56, 239, 125, 0.15)' : 'rgba(255, 212, 59, 0.15)',
                      color: invoice.status === 'completed' ? '#11998e' : '#f59f00',
                    }}>
                      {invoice.status === 'completed' ? 'שולם' : 'ממתין'}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Profiles Card */}
        <div style={{
          background: colors.bgSecondary,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          padding: '12px',
        }}>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '13px',
            fontWeight: 700,
            color: colors.textPrimary,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <i className="bi bi-people" style={{ fontSize: '11px', color: '#fff' }}></i>
            </div>
            פרופילים
            <span style={{
              background: 'rgba(79, 172, 254, 0.15)',
              color: '#1971c2',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
            }}>
              {profiles.length}
            </span>
          </h3>

          <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
            {profiles.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '16px 12px',
                color: colors.textMuted,
              }}>
                <i className="bi bi-person-slash" style={{ fontSize: '20px', opacity: 0.5 }}></i>
                <div style={{ marginTop: '6px', fontSize: '11px' }}>אין פרופילים</div>
              </div>
            ) : (
              profiles.map((profile, index) => {
                const isMainProfile = !profile.parent_profile
                const isActive = isMainProfile
                  ? profile.last_health_check && moment(profile.last_health_check).isAfter(moment().subtract(5, 'minutes'))
                  : profile.parent_profile_on

                const gradients = [
                  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                ]

                return (
                  <div
                    key={profile._id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 8px',
                      marginBottom: '4px',
                      background: colors.bgTertiary,
                      borderRadius: '6px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: '6px',
                        background: gradients[index % gradients.length],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        fontSize: '10px',
                        color: '#fff',
                      }}>
                        {(profile.first_name || profile.facebook_username || 'P').charAt(0).toUpperCase()}
                      </div>
                      <div style={{ fontWeight: 500, color: colors.textPrimary, fontSize: '11px' }}>
                        {profile.first_name || profile.facebook_username || 'פרופיל'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {/* Posts per day - editable for all active profiles */}
                      {isActive && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <input
                            type="number"
                            value={profile.posts_per_day || 90}
                            onChange={async (e) => {
                              const val = parseInt(e.target.value) || 90;
                              try {
                                await updateProfilePostsPerDay(account!._id, profile._id, val);
                                const updatedProfiles = profiles.map((p: any) =>
                                  p._id === profile._id ? {...p, posts_per_day: val} : p
                                );
                                setProfiles(updatedProfiles);
                              } catch (err) {
                                console.error('Failed to update posts_per_day:', err);
                              }
                            }}
                            min={1}
                            max={500}
                            title="מקסימום פוסטים ביום"
                            style={{
                              width: '44px',
                              padding: '2px 4px',
                              border: '1px solid ' + colors.border,
                              borderRadius: '4px',
                              fontSize: '10px',
                              textAlign: 'center',
                              background: colors.bgTertiary,
                              color: colors.textSecondary,
                            }}
                          />
                          <span style={{ fontSize: '9px', color: colors.textMuted }}>/יום</span>
                        </div>
                      )}
                      {isMainProfile ? (
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '9px',
                          fontWeight: 600,
                          background: isActive ? 'rgba(56, 239, 125, 0.15)' : 'rgba(108, 114, 147, 0.15)',
                          color: isActive ? '#38ef7d' : '#6c7293',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                        }}>
                          <div style={{
                            width: '4px',
                            height: '4px',
                            borderRadius: '50%',
                            background: isActive ? '#38ef7d' : '#6c7293',
                          }}></div>
                          {isActive ? 'מחובר' : 'לא פעיל'}
                        </span>
                      ) : (
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                        }}>
                          <div style={{
                            width: '28px',
                            height: '16px',
                            borderRadius: '8px',
                            background: profile.parent_profile_on
                              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                              : 'rgba(108, 114, 147, 0.3)',
                            position: 'relative',
                            transition: 'all 0.3s ease',
                          }}>
                            <div style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '6px',
                              background: '#fff',
                              position: 'absolute',
                              top: '2px',
                              left: profile.parent_profile_on ? '2px' : '14px',
                              transition: 'all 0.3s ease',
                            }}></div>
                          </div>
                          <input
                            type="checkbox"
                            checked={profile.parent_profile_on || false}
                            onChange={(e) => handleToggleProfile(profile._id, e.target.checked)}
                            style={{ display: 'none' }}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Logs Card */}
        {/* Important Admin Notes */}
        <div style={{
          background: colors.bgSecondary,
          borderRadius: '12px',
          border: `1px solid #ffc107`,
          padding: '12px',
          marginBottom: '12px',
        }}>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '13px',
            fontWeight: 700,
            color: colors.textPrimary,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            📌 הערות חשובות
            <span style={{
              background: 'rgba(255, 193, 7, 0.2)',
              color: '#b8860b',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
            }}>
              {(account as any).important_notes?.length || 0}
            </span>
          </h3>
          <form onSubmit={async (e) => {
            e.preventDefault()
            const input = (e.target as any).elements.noteText
            if (!input.value.trim()) return
            try {
              const res = await axios.post(`${process.env.REACT_APP_API_URL}/admin/account-notes/${id}`, { text: input.value.trim() }, {
                headers: { Authorization: `Bearer ${getAdminToken()}` }, withCredentials: true
              })
              setAccount((prev: any) => prev ? { ...prev, important_notes: res.data.admin_notes } : prev)
              input.value = ''
            } catch { alert('שגיאה בשמירת הערה') }
          }} style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
            <input
              name="noteText"
              type="text"
              placeholder="הערה חשובה חדשה..."
              style={{
                flex: 1,
                padding: '6px 10px',
                borderRadius: '6px',
                border: `1px solid ${colors.border}`,
                background: colors.bgTertiary,
                color: colors.textPrimary,
                fontSize: '11px',
                outline: 'none',
              }}
            />
            <button type="submit" style={{
              width: '30px', height: '30px', borderRadius: '6px', border: 'none',
              background: '#ffc107', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="bi bi-plus" style={{ fontSize: '14px' }}></i>
            </button>
          </form>
          <div style={{ maxHeight: '140px', overflowY: 'auto' }}>
            {!((account as any).important_notes?.length > 0) ? (
              <div style={{ textAlign: 'center', padding: '8px', color: colors.textMuted, fontSize: '10px' }}>
                אין הערות חשובות
              </div>
            ) : (
              [...(account as any).important_notes].reverse().map((note: any) => (
                <div key={note._id} style={{
                  padding: '6px 8px', marginBottom: '4px',
                  background: '#fff8e1', borderRadius: '6px', border: '1px solid #ffecb3',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '11px', fontWeight: 500, color: colors.textPrimary }}>{note.text}</div>
                    <div style={{ fontSize: '9px', color: '#999', marginTop: '2px' }}>
                      {new Date(note.created_at).toLocaleDateString('he-IL')} {new Date(note.created_at).toLocaleTimeString('he-IL', {hour: '2-digit', minute: '2-digit'})}
                      {note.admin?.username && ` • ${note.admin.username}`}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm('למחוק הערה זו?')) return
                      try {
                        const res = await axios.delete(`${process.env.REACT_APP_API_URL}/admin/account-notes/${id}/${note._id}`, {
                          headers: { Authorization: `Bearer ${getAdminToken()}` }, withCredentials: true
                        })
                        setAccount((prev: any) => prev ? { ...prev, important_notes: res.data.admin_notes } : prev)
                      } catch { alert('שגיאה במחיקה') }
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: '12px', padding: '0 2px' }}
                    title="מחק"
                  >×</button>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{
          background: colors.bgSecondary,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          padding: '12px',
        }}>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '13px',
            fontWeight: 700,
            color: colors.textPrimary,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <i className="bi bi-chat-text" style={{ fontSize: '11px', color: '#fff' }}></i>
            </div>
            הערות ולוג
            <span style={{
              background: 'rgba(240, 147, 251, 0.15)',
              color: '#d63384',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
            }}>
              {logs.length}
            </span>
          </h3>

          {/* Add Log Form */}
          <form onSubmit={handleAddLog} style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
              <input
                type="text"
                placeholder="נושא"
                value={newLogSubject}
                onChange={(e) => setNewLogSubject(e.target.value)}
                style={{
                  width: '40%',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: `1px solid ${colors.border}`,
                  background: colors.bgTertiary,
                  color: colors.textPrimary,
                  fontSize: '11px',
                  outline: 'none',
                }}
              />
              <input
                type="text"
                placeholder="הוסף הערה..."
                value={newLogDescription}
                onChange={(e) => setNewLogDescription(e.target.value)}
                style={{
                  flex: 1,
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: `1px solid ${colors.border}`,
                  background: colors.bgTertiary,
                  color: colors.textPrimary,
                  fontSize: '11px',
                  outline: 'none',
                }}
              />
              <button
                type="submit"
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '6px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="bi bi-plus" style={{ fontSize: '14px' }}></i>
              </button>
            </div>
          </form>

          <div style={{ maxHeight: '140px', overflowY: 'auto' }}>
            {logs.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '16px 12px',
                color: colors.textMuted,
              }}>
                <i className="bi bi-chat-square" style={{ fontSize: '18px', opacity: 0.5 }}></i>
                <div style={{ marginTop: '6px', fontSize: '10px' }}>אין הערות</div>
              </div>
            ) : (
              logs.map(log => (
                <div
                  key={log._id}
                  style={{
                    padding: '6px 8px',
                    marginBottom: '4px',
                    background: colors.bgTertiary,
                    borderRadius: '6px',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: 600,
                        fontSize: '10px',
                        color: colors.textPrimary,
                      }}>
                        {log.subject}
                      </div>
                      {log.description && (
                        <div style={{
                          fontSize: '9px',
                          color: colors.textSecondary,
                          lineHeight: 1.3,
                          whiteSpace: 'pre-wrap',
                          maxHeight: '32px',
                          overflow: 'hidden',
                        }}>
                          {log.description}
                        </div>
                      )}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '9px',
                        color: colors.textMuted,
                        marginTop: '2px',
                      }}>
                        <span>{log.created_by?.username || 'מערכת'}</span>
                        <span>•</span>
                        <span>{moment(log.created_at).format('DD/MM HH:mm')}</span>
                      </div>
                    </div>
                    {log.created_by && (
                      <button
                        onClick={() => handleDeleteLog(log._id)}
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          border: 'none',
                          background: 'rgba(255, 107, 107, 0.1)',
                          color: '#ff6b6b',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <i className="bi bi-x" style={{ fontSize: '12px' }}></i>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Package Timeline Card */}
        <div style={{
          background: colors.bgSecondary,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          padding: '12px',
        }}>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '13px',
            fontWeight: 700,
            color: colors.textPrimary,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #ffd43b 0%, #f59f00 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <i className="bi bi-box-seam" style={{ fontSize: '11px', color: '#fff' }}></i>
            </div>
            היסטוריית חבילות
            <span style={{
              background: 'rgba(255, 212, 59, 0.15)',
              color: '#f59f00',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
            }}>
              {packageChanges.length}
            </span>
          </h3>

          {/* Add Slot Button */}
          <button
            onClick={() => setShowAddSlotModal(true)}
            style={{
              width: '100%',
              padding: '8px',
              marginBottom: '8px',
              borderRadius: '6px',
              border: `1px dashed ${colors.border}`,
              background: colors.bgTertiary,
              color: colors.textSecondary,
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
            }}
          >
            <i className="bi bi-plus"></i> הוסף סלוט
          </button>

          <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
            {loadingSlots ? (
              <div style={{ textAlign: 'center', padding: '16px', color: colors.textMuted }}>
                טוען...
              </div>
            ) : packageChanges.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '16px 12px',
                color: colors.textMuted,
              }}>
                <i className="bi bi-clock-history" style={{ fontSize: '20px', opacity: 0.5 }}></i>
                <div style={{ marginTop: '6px', fontSize: '11px' }}>אין שינויים עדיין</div>
              </div>
            ) : (
              packageChanges.map((change: any) => (
                <div
                  key={change._id}
                  style={{
                    padding: '6px 8px',
                    marginBottom: '4px',
                    background: colors.bgTertiary,
                    borderRadius: '6px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      color: change.change_type === 'slot_added' ? '#11998e' :
                             change.change_type === 'slot_removed' ? '#ff6b6b' : colors.textPrimary
                    }}>
                      {change.change_type === 'slot_added' && 'סלוט נוסף'}
                      {change.change_type === 'slot_removed' && 'סלוט הוסר'}
                      {change.change_type === 'profile_assigned' && 'פרופיל שויך'}
                      {change.change_type === 'profile_unassigned' && 'פרופיל הוסר'}
                    </span>
                    <span style={{ fontSize: '9px', color: colors.textMuted }}>
                      {moment(change.created_at).format('DD/MM HH:mm')}
                    </span>
                  </div>
                  <div style={{ fontSize: '9px', color: colors.textSecondary }}>
                    סלוט {change.slot_type === 'profile' ? 'פרופיל' : 'תת-פרופיל'}
                    {change.is_free && ' (חינם)'}
                    {change.monthly_change ? ` | חודשי: ${change.monthly_change > 0 ? '+' : ''}${change.monthly_change} ₪` : ''}
                  </div>
                  {change.reason && (
                    <div style={{ fontSize: '9px', color: colors.textMuted, marginTop: '2px' }}>
                      {change.reason}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Plan Payments Card */}
        <div style={{
          background: colors.bgSecondary,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          padding: '12px',
        }}>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '13px',
            fontWeight: 700,
            color: colors.textPrimary,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <i className="bi bi-cash-stack" style={{ fontSize: '11px', color: '#fff' }}></i>
            </div>
            תשלומים
            <span style={{
              background: 'rgba(79, 172, 254, 0.15)',
              color: '#4facfe',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
            }}>
              {planPayments.length}
            </span>
          </h3>

          <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
            {loadingSlots ? (
              <div style={{ textAlign: 'center', padding: '16px', color: colors.textMuted }}>
                טוען...
              </div>
            ) : planPayments.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '16px 12px',
                color: colors.textMuted,
              }}>
                <i className="bi bi-wallet2" style={{ fontSize: '20px', opacity: 0.5 }}></i>
                <div style={{ marginTop: '6px', fontSize: '11px' }}>אין תשלומים עדיין</div>
              </div>
            ) : (
              planPayments.map((payment: any) => (
                <div
                  key={payment._id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 8px',
                    marginBottom: '4px',
                    background: colors.bgTertiary,
                    borderRadius: '6px',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 500, color: colors.textPrimary }}>
                      {payment.description || payment.payment_type}
                    </div>
                    <div style={{ fontSize: '9px', color: colors.textMuted }}>
                      {moment(payment.created_at).format('DD/MM/YY HH:mm')}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontWeight: 700,
                      fontSize: '11px',
                      color: payment.payment_type === 'refund' ? '#ff6b6b' : '#11998e'
                    }}>
                      {payment.payment_type === 'refund' ? '-' : ''}${payment.amount}
                    </div>
                    <div style={{
                      fontSize: '9px',
                      fontWeight: 600,
                      color: payment.status === 'completed' ? '#11998e' :
                             payment.status === 'failed' ? '#ff6b6b' : '#f59f00'
                    }}>
                      {payment.status === 'completed' ? 'הושלם' :
                       payment.status === 'failed' ? 'נכשל' : 'ממתין'}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Transfer Campaigns Section */}
      <div style={{
        background: colors.bgSecondary,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        padding: '12px',
        marginTop: '12px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '12px',
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <i className="bi bi-arrow-left-right" style={{ fontSize: '14px', color: '#fff' }}></i>
          </div>
          <span style={{ fontWeight: 600, fontSize: '14px', color: colors.textPrimary }}>
            העברת קמפיינים
          </span>
        </div>

        <div ref={transferRef} style={{ position: 'relative', marginBottom: '10px' }}>
          <input
            type="text"
            placeholder="חפש חשבון יעד לפי שם, אימייל או טלפון..."
            value={transferSearch}
            onChange={(e) => setTransferSearch(e.target.value)}
            onFocus={() => setTransferDropdownOpen(true)}
            disabled={transferring}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${transferDropdownOpen ? '#f5576c' : colors.border}`,
              fontSize: '13px',
              outline: 'none',
              background: colors.bgPrimary,
              opacity: transferring ? 0.6 : 1,
            }}
          />
          {transferDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: '#fff',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              marginTop: '4px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              zIndex: 1050,
              overflow: 'hidden',
            }}>
              <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {(() => {
                  const q = transferSearch.toLowerCase()
                  const filtered = allAccountsForSharing.filter(a => {
                    if (a._id === id) return false
                    if (!q) return false
                    return (a.first_name || '').toLowerCase().includes(q) ||
                      (a.last_name || '').toLowerCase().includes(q) ||
                      (a.email || '').toLowerCase().includes(q) ||
                      (a.phone || '').toLowerCase().includes(q)
                  }).slice(0, 30)

                  if (!transferSearch.trim()) {
                    return (
                      <div style={{ padding: '16px', textAlign: 'center', color: colors.textMuted, fontSize: '13px' }}>
                        הקלד לחיפוש חשבון יעד...
                      </div>
                    )
                  }
                  if (filtered.length === 0) {
                    return (
                      <div style={{ padding: '16px', textAlign: 'center', color: colors.textMuted, fontSize: '13px' }}>
                        לא נמצאו חשבונות
                      </div>
                    )
                  }
                  return filtered.map((acc: any) => (
                    <div
                      key={acc._id}
                      onClick={() => {
                        setTransferTarget(acc)
                        setTransferDropdownOpen(false)
                        setTransferSearch(`${acc.first_name || ''} ${acc.last_name || ''}`.trim() || acc.email)
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${colors.borderLight}`,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa' }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: '11px',
                          fontWeight: 700,
                        }}>
                          {(acc.first_name || acc.email || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>
                            {acc.first_name} {acc.last_name}
                          </div>
                          <div style={{ fontSize: '11px', color: colors.textMuted }}>
                            {acc.email}{acc.phone ? ` · ${acc.phone}` : ''}
                          </div>
                        </div>
                      </div>
                      <i className="bi bi-arrow-right" style={{ fontSize: '14px', color: '#f5576c' }}></i>
                    </div>
                  ))
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Selected target + transfer button */}
        {transferTarget && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            borderRadius: '8px',
            background: 'rgba(245, 87, 108, 0.06)',
            border: '1px solid rgba(245, 87, 108, 0.2)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 700,
              }}>
                {(transferTarget.first_name || transferTarget.email || '?')[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 500, color: colors.textPrimary }}>
                  {transferTarget.first_name} {transferTarget.last_name}
                </div>
                <div style={{ fontSize: '11px', color: colors.textMuted }}>{transferTarget.email}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                onClick={() => { setTransferTarget(null); setTransferSearch('') }}
                style={{
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: `1px solid ${colors.border}`,
                  background: colors.bgTertiary,
                  color: colors.textMuted,
                  cursor: 'pointer',
                  fontSize: '11px',
                }}
              >
                <i className="bi bi-x"></i>
              </button>
              <button
                onClick={() => setShowTransferConfirm(true)}
                disabled={transferring}
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: '#fff',
                  cursor: transferring ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  opacity: transferring ? 0.7 : 1,
                }}
              >
                {transferring ? 'מעביר...' : 'העבר קמפיינים'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Library Sharing Section */}
      <div style={{
        background: colors.bgSecondary,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        padding: '12px',
        marginTop: '12px',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <i className="bi bi-share" style={{ fontSize: '14px', color: '#fff' }}></i>
            </div>
            <span style={{ fontWeight: 600, fontSize: '14px', color: colors.textPrimary }}>
              שיתוף ספריות
            </span>
            <span style={{
              background: 'rgba(56, 239, 125, 0.1)',
              color: '#11998e',
              padding: '2px 8px',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: 600,
            }}>
              {sharedAccounts.length}
            </span>
          </div>
          <button
            onClick={async () => {
              if (!id) return
              setSyncingLibraries(true)
              try {
                const res = await syncLibrarySharing(id)
                alert(`Synced ${res.data.synced} libraries`)
              } catch (e) {
                alert('Failed to sync')
              }
              setSyncingLibraries(false)
            }}
            disabled={syncingLibraries || sharedAccounts.length === 0}
            style={{
              padding: '4px 12px',
              borderRadius: '8px',
              border: 'none',
              background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
              color: '#fff',
              cursor: syncingLibraries || sharedAccounts.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '11px',
              fontWeight: 600,
              opacity: syncingLibraries || sharedAccounts.length === 0 ? 0.5 : 1,
            }}
          >
            {syncingLibraries ? 'מסנכרן...' : 'סנכרן ספריות'}
          </button>
        </div>

        {/* Multi-select search dropdown */}
        <div ref={sharingRef} style={{ position: 'relative', marginBottom: '12px' }}>
          <input
            type="text"
            placeholder="חפש לפי שם, אימייל או טלפון..."
            value={sharingSearch}
            onChange={(e) => setSharingSearch(e.target.value)}
            onFocus={() => setSharingDropdownOpen(true)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${sharingDropdownOpen ? '#667eea' : colors.border}`,
              fontSize: '13px',
              outline: 'none',
              background: colors.bgPrimary,
            }}
          />
          {sharingDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: '#fff',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              marginTop: '4px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              zIndex: 1050,
              overflow: 'hidden',
            }}>
              <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {(() => {
                  const q = sharingSearch.toLowerCase()
                  const filtered = allAccountsForSharing.filter(a => {
                    if (a._id === id) return false
                    if (!q) return false
                    return (a.first_name || '').toLowerCase().includes(q) ||
                      (a.last_name || '').toLowerCase().includes(q) ||
                      (a.email || '').toLowerCase().includes(q) ||
                      (a.phone || '').toLowerCase().includes(q)
                  }).slice(0, 30)

                  if (!sharingSearch.trim()) {
                    return (
                      <div style={{ padding: '16px', textAlign: 'center', color: colors.textMuted, fontSize: '13px' }}>
                        הקלד לחיפוש חשבונות...
                      </div>
                    )
                  }
                  if (filtered.length === 0) {
                    return (
                      <div style={{ padding: '16px', textAlign: 'center', color: colors.textMuted, fontSize: '13px' }}>
                        לא נמצאו חשבונות
                      </div>
                    )
                  }
                  return filtered.map((acc: any) => {
                    const isShared = sharedAccounts.some(a => a._id === acc._id)
                    const isLoading = addingSharing === acc._id
                    return (
                      <div
                        key={acc._id}
                        onClick={() => handleToggleSharing(acc)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderBottom: `1px solid ${colors.borderLight}`,
                          background: isShared ? 'rgba(56, 239, 125, 0.05)' : 'transparent',
                        }}
                        onMouseEnter={(e) => { if (!isShared) e.currentTarget.style.backgroundColor = '#f8f9fa' }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isShared ? 'rgba(56, 239, 125, 0.05)' : 'transparent' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '8px',
                            background: isShared
                              ? 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)'
                              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 700,
                          }}>
                            {isShared ? <i className="bi bi-check" style={{ fontSize: '14px' }}></i>
                              : (acc.first_name || acc.email || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>
                              {acc.first_name} {acc.last_name}
                            </div>
                            <div style={{ fontSize: '11px', color: colors.textMuted }}>
                              {acc.email}{acc.phone ? ` · ${acc.phone}` : ''}
                            </div>
                          </div>
                        </div>
                        {isLoading ? (
                          <span className="spinner-border spinner-border-sm text-primary" />
                        ) : isShared ? (
                          <i className="bi bi-x-circle" style={{ fontSize: '16px', color: '#ff6b6b' }}></i>
                        ) : (
                          <i className="bi bi-plus-circle" style={{ fontSize: '16px', color: '#11998e' }}></i>
                        )}
                      </div>
                    )
                  })
                })()}
              </div>
              {sharingSearch.trim() && (
                <div style={{
                  padding: '6px',
                  borderTop: `1px solid ${colors.borderLight}`,
                  background: colors.bgPrimary,
                  textAlign: 'center',
                }}>
                  <span style={{ fontSize: '11px', color: colors.textMuted }}>
                    לחץ להוסיף/להסיר שיתוף
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected shared accounts */}
        {sharedAccounts.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '12px',
            color: colors.textMuted,
            fontSize: '13px',
          }}>
            אין חשבונות משותפים. חפש ובחר חשבונות למעלה.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {sharedAccounts.map((acc: any) => (
              <div
                key={acc._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  background: 'rgba(56, 239, 125, 0.08)',
                  border: '1px solid rgba(17, 153, 142, 0.2)',
                  fontSize: '12px',
                  color: colors.textPrimary,
                }}
              >
                <span style={{ fontWeight: 500 }}>
                  {acc.first_name} {acc.last_name}
                </span>
                <span style={{ color: colors.textMuted, fontSize: '11px' }}>{acc.email}</span>
                <button
                  onClick={() => handleToggleSharing(acc)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0 2px',
                    color: '#ff6b6b',
                    fontSize: '14px',
                    lineHeight: 1,
                  }}
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Child Accounts / Billing Section */}
      <div style={{
        background: colors.bgSecondary,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        padding: '12px',
        marginTop: '12px',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <i className="bi bi-people" style={{ fontSize: '14px', color: '#fff' }}></i>
            </div>
            <span style={{ fontWeight: 600, fontSize: '14px', color: colors.textPrimary }}>
              חשבונות ילדים (חיוב)
            </span>
            <span style={{
              background: 'rgba(102, 126, 234, 0.1)',
              color: '#667eea',
              padding: '2px 8px',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: 600,
            }}>
              {childAccounts.length}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {childAccounts.length > 0 && (
              <button
                onClick={handleSyncChildTrials}
                disabled={syncingTrials}
                title="סנכרן את תאריך סיום הניסיון של כל חשבונות הילדים לתאריך של ההורה"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  border: `1px solid ${colors.border}`,
                  background: colors.bgPrimary,
                  color: '#667eea',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: syncingTrials ? 'default' : 'pointer',
                  opacity: syncingTrials ? 0.6 : 1,
                }}
              >
                <i className={`bi ${syncingTrials ? 'bi-arrow-repeat' : 'bi-calendar-check'}`}></i>
                {syncingTrials ? 'מסנכרן...' : 'סנכרן תוקף ניסיון'}
              </button>
            )}
            <span style={{ fontSize: '12px', color: colors.textSecondary }}>יום חיוב:</span>
            <select
              value={billingDay}
              onChange={async (e) => {
                const day = parseInt(e.target.value)
                setBillingDay(day)
                if (id) await updateBillingDay(id, day).catch(() => {})
              }}
              style={{
                padding: '3px 8px',
                borderRadius: '6px',
                border: `1px solid ${colors.border}`,
                fontSize: '12px',
                background: colors.bgPrimary,
              }}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Multi-select search dropdown for children */}
        <div ref={childRef} style={{ position: 'relative', marginBottom: '12px' }}>
          <input
            type="text"
            placeholder="חפש חשבונות להוספה כילדים..."
            value={childSearch}
            onChange={(e) => setChildSearch(e.target.value)}
            onFocus={() => setChildDropdownOpen(true)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${childDropdownOpen ? '#667eea' : colors.border}`,
              fontSize: '13px',
              outline: 'none',
              background: colors.bgPrimary,
            }}
          />
          {childDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: '#fff',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              marginTop: '4px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              zIndex: 1050,
              overflow: 'hidden',
            }}>
              <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                {(() => {
                  const q = childSearch.toLowerCase()
                  const filtered = allAccountsForSharing.filter(a => {
                    if (a._id === id) return false
                    if (!q) return false
                    return (a.first_name || '').toLowerCase().includes(q) ||
                      (a.last_name || '').toLowerCase().includes(q) ||
                      (a.email || '').toLowerCase().includes(q) ||
                      (a.phone || '').toLowerCase().includes(q)
                  }).slice(0, 30)

                  if (!childSearch.trim()) {
                    return (
                      <div style={{ padding: '16px', textAlign: 'center', color: colors.textMuted, fontSize: '13px' }}>
                        הקלד לחיפוש חשבונות...
                      </div>
                    )
                  }
                  if (filtered.length === 0) {
                    return (
                      <div style={{ padding: '16px', textAlign: 'center', color: colors.textMuted, fontSize: '13px' }}>
                        לא נמצאו חשבונות
                      </div>
                    )
                  }
                  return filtered.map((acc: any) => {
                    const isChild = childAccounts.some(a => a._id === acc._id)
                    const isLoading = addingChild === acc._id
                    return (
                      <div
                        key={acc._id}
                        onClick={() => handleToggleChild(acc)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderBottom: `1px solid ${colors.borderLight}`,
                          background: isChild ? 'rgba(102, 126, 234, 0.05)' : 'transparent',
                        }}
                        onMouseEnter={(e) => { if (!isChild) e.currentTarget.style.backgroundColor = '#f8f9fa' }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isChild ? 'rgba(102, 126, 234, 0.05)' : 'transparent' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '8px',
                            background: isChild
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : `linear-gradient(135deg, ${colors.textMuted} 0%, ${colors.textSecondary} 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: 700,
                          }}>
                            {isChild ? <i className="bi bi-check" style={{ fontSize: '14px' }}></i>
                              : (acc.first_name || acc.email || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>
                              {acc.first_name} {acc.last_name}
                            </div>
                            <div style={{ fontSize: '11px', color: colors.textMuted }}>
                              {acc.email}{acc.phone ? ` · ${acc.phone}` : ''}
                            </div>
                          </div>
                        </div>
                        {isLoading ? (
                          <span className="spinner-border spinner-border-sm text-primary" />
                        ) : isChild ? (
                          <i className="bi bi-x-circle" style={{ fontSize: '16px', color: '#ff6b6b' }}></i>
                        ) : (
                          <i className="bi bi-plus-circle" style={{ fontSize: '16px', color: '#667eea' }}></i>
                        )}
                      </div>
                    )
                  })
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Child accounts list with billing amounts */}
        {childAccounts.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '12px',
            color: colors.textMuted,
            fontSize: '13px',
          }}>
            אין חשבונות ילדים. חפש והוסף חשבונות למעלה.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {childAccounts.map((acc: any) => (
                <div
                  key={acc._id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: colors.bgPrimary,
                    border: `1px solid ${colors.borderLight}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontSize: '11px',
                      fontWeight: 700,
                    }}>
                      {(acc.first_name || acc.email || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>
                        {acc.first_name} {acc.last_name}
                      </div>
                      <div style={{ fontSize: '11px', color: colors.textMuted }}>
                        {acc.email}
                        {acc.monthly_payment_amount ? ` · ${acc.monthly_payment_amount} ₪/mo` : ''}
                        {acc.trial_period_end ? ` · ${new Date(acc.trial_period_end) > new Date() ? 'Active' : 'Expired'} ${moment(acc.trial_period_end).format('DD/MM/YY')}` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {editingChildAmount === acc._id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <input
                          type="number"
                          value={editingAmount}
                          onChange={(e) => setEditingAmount(parseFloat(e.target.value) || 0)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleUpdateChildAmount(acc._id, editingAmount)
                            if (e.key === 'Escape') setEditingChildAmount(null)
                          }}
                          autoFocus
                          style={{
                            width: '70px',
                            padding: '3px 6px',
                            borderRadius: '6px',
                            border: `1px solid ${colors.border}`,
                            fontSize: '12px',
                            textAlign: 'center',
                          }}
                        />
                        <button
                          onClick={() => handleUpdateChildAmount(acc._id, editingAmount)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#11998e', fontSize: '14px', padding: '2px',
                          }}
                        >
                          <i className="bi bi-check"></i>
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => { setEditingChildAmount(acc._id); setEditingAmount(acc.monthly_amount || 0) }}
                        style={{
                          padding: '3px 10px',
                          borderRadius: '6px',
                          background: 'rgba(102, 126, 234, 0.1)',
                          color: '#667eea',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                        title="Click to edit amount"
                      >
                        {acc.monthly_payment_amount || acc.monthly_amount || 0} &#8362;/mo
                      </div>
                    )}
                    <button
                      onClick={() => handleToggleChild(acc)}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'rgba(255, 107, 107, 0.1)',
                        color: '#ff6b6b',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* Total */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '12px',
              marginTop: '10px',
              paddingTop: '10px',
              borderTop: `1px solid ${colors.borderLight}`,
            }}>
              <span style={{ fontSize: '13px', color: colors.textSecondary }}>Total monthly:</span>
              <span style={{
                fontSize: '15px',
                fontWeight: 700,
                color: '#667eea',
              }}>
                {(account?.monthly_payment_amount || 0) + childAccounts.reduce((sum: number, c: any) => sum + (c.monthly_payment_amount || c.monthly_amount || 0), 0)} &#8362;
              </span>
            </div>
          </>
        )}
      </div>

      {/* Add Slot Modal */}
      {showAddSlotModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: colors.bgSecondary,
              borderRadius: '20px',
              padding: '24px',
              width: '360px',
              border: `1px solid ${colors.border}`,
              boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ffd43b 0%, #f59f00 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <i className="bi bi-plus-circle" style={{ fontSize: '18px', color: '#fff' }}></i>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                  הוספת סלוט
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textMuted }}>
                  {getAccountName()}
                </p>
              </div>
            </div>

            {/* Slot Type */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: colors.textSecondary,
                fontSize: '12px',
                fontWeight: 500,
              }}>
                סוג סלוט
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setAddSlotType('profile')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: `1px solid ${addSlotType === 'profile' ? '#667eea' : colors.border}`,
                    background: addSlotType === 'profile' ? 'rgba(102, 126, 234, 0.1)' : colors.bgTertiary,
                    color: addSlotType === 'profile' ? '#667eea' : colors.textSecondary,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  פרופיל (190 ₪)
                </button>
                <button
                  onClick={() => setAddSlotType('subprofile')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: `1px solid ${addSlotType === 'subprofile' ? '#667eea' : colors.border}`,
                    background: addSlotType === 'subprofile' ? 'rgba(102, 126, 234, 0.1)' : colors.bgTertiary,
                    color: addSlotType === 'subprofile' ? '#667eea' : colors.textSecondary,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  תת-פרופיל (100 ₪)
                </button>
              </div>
            </div>

            {/* Free Toggle */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={addSlotFree}
                  onChange={(e) => setAddSlotFree(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ color: colors.textPrimary, fontSize: '13px' }}>
                  סלוט חינם (ללא חיוב חודשי)
                </span>
              </label>
            </div>

            {/* Reason */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: colors.textSecondary,
                fontSize: '12px',
                fontWeight: 500,
              }}>
                סיבה (אופציונלי)
              </label>
              <input
                type="text"
                value={addSlotReason}
                onChange={(e) => setAddSlotReason(e.target.value)}
                placeholder="לדוגמה: סלוט מבצע, לקוח VIP"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  background: colors.bgTertiary,
                  color: colors.textPrimary,
                  fontSize: '13px',
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAddSlotModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: `1px solid ${colors.border}`,
                  background: colors.bgTertiary,
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                ביטול
              </button>
              <button
                onClick={handleAddSlot}
                disabled={addingSlot}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #ffd43b 0%, #f59f00 100%)',
                  color: '#fff',
                  cursor: addingSlot ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  opacity: addingSlot ? 0.7 : 1,
                }}
              >
                {addingSlot ? 'מוסיף...' : 'הוסף סלוט'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* One-off charge modal — charges the saved card and issues an invoice with
          the typed reason. Explicitly grants NO subscription time; the copy says so
          in two places so it can't be mistaken for a renewal. */}
      {showChargeModal && account.has_payment_token && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: colors.bgSecondary,
              borderRadius: '20px',
              padding: '24px',
              width: '420px',
              border: `1px solid ${colors.border}`,
              boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <i className="bi bi-credit-card" style={{ fontSize: '18px', color: '#3d2c00' }}></i>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                  חיוב חד-פעמי
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textMuted }}>
                  {getAccountName()}
                  {account.payment_card_last4 ? ` · כרטיס ••${account.payment_card_last4}` : ''}
                </p>
              </div>
            </div>

            <div style={{
              padding: '10px 12px',
              background: 'rgba(247, 151, 30, 0.08)',
              border: '1px solid rgba(247, 151, 30, 0.25)',
              borderRadius: '10px',
              marginBottom: '16px',
              color: colors.textSecondary,
              fontSize: '11.5px',
              lineHeight: 1.5,
            }}>
              הכרטיס השמור יחויב מיידית ותופק חשבונית עם הסיבה שתזינו.
              <b style={{ color: colors.textPrimary }}> לא יתווספו ימים לתקופת המנוי.</b>
            </div>

            <label style={{ display: 'block', color: colors.textSecondary, fontSize: '11px', marginBottom: '4px' }}>סכום לחיוב (₪, כולל מע״מ)</label>
            <input
              type='number'
              min={1}
              step='0.01'
              value={chargeAmount}
              onChange={(e) => setChargeAmount(e.target.value)}
              placeholder='למשל 250'
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                background: colors.bgTertiary,
                color: colors.textPrimary,
                fontSize: '14px',
                marginBottom: '12px',
                outline: 'none',
              }}
            />

            <label style={{ display: 'block', color: colors.textSecondary, fontSize: '11px', marginBottom: '4px' }}>סיבת החיוב (תופיע בחשבונית ללקוח)</label>
            <input
              type='text'
              value={chargeReason}
              onChange={(e) => setChargeReason(e.target.value)}
              placeholder='למשל: פרופיל נוסף, שירות עיצוב, יתרת חוב'
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                background: colors.bgTertiary,
                color: colors.textPrimary,
                fontSize: '13px',
                marginBottom: '12px',
                outline: 'none',
              }}
            />

            <label style={{ display: 'block', color: colors.textSecondary, fontSize: '11px', marginBottom: '4px' }}>תשלומים</label>
            <input
              type='number'
              min={1}
              max={36}
              value={chargeInstallments}
              onChange={(e) => setChargeInstallments(Math.min(36, Math.max(1, parseInt(e.target.value) || 1)))}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                background: colors.bgTertiary,
                color: colors.textPrimary,
                fontSize: '13px',
                marginBottom: '12px',
                outline: 'none',
              }}
            />

            {chargeResult && (
              <div style={{
                padding: '10px 12px',
                borderRadius: '8px',
                marginBottom: '12px',
                fontSize: '12px',
                background: chargeResult.ok ? 'rgba(56, 239, 125, 0.1)' : 'rgba(255, 107, 107, 0.1)',
                border: `1px solid ${chargeResult.ok ? 'rgba(56, 239, 125, 0.3)' : 'rgba(255, 107, 107, 0.3)'}`,
                color: chargeResult.ok ? '#11998e' : '#ff6b6b',
              }}>
                {chargeResult.message}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleCustomCharge}
                disabled={charging}
                style={{
                  flex: 1,
                  padding: '11px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
                  color: '#3d2c00',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: charging ? 'not-allowed' : 'pointer',
                  opacity: charging ? 0.6 : 1,
                }}
              >
                {charging ? 'מחייב…' : `חייב ₪${parseFloat(chargeAmount) > 0 ? parseFloat(chargeAmount) : 0}`}
              </button>
              <button
                onClick={() => { setShowChargeModal(false); setChargeResult(null) }}
                disabled={charging}
                style={{
                  flex: 1,
                  padding: '11px',
                  borderRadius: '8px',
                  border: `1px solid ${colors.border}`,
                  background: colors.bgTertiary,
                  color: colors.textSecondary,
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: charging ? 'not-allowed' : 'pointer',
                }}
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Period Modal */}
      {showTrialModal && (() => {
        const currentEnd = account.trial_period_end ? moment(account.trial_period_end) : moment()
        const daysRemaining = currentEnd.diff(moment(), 'days')
        const newDate = trialMode === 'date' && trialDate
          ? moment(trialDate)
          : moment(account.trial_period_end || new Date()).add(trialDays, 'days')

        return (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: colors.bgSecondary,
                borderRadius: '20px',
                padding: '24px',
                width: '400px',
                border: `1px solid ${colors.border}`,
                boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <i className="bi bi-calendar-plus" style={{ fontSize: '18px', color: '#fff' }}></i>
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                    שינוי תקופת מנוי
                  </h3>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textMuted }}>
                    {getAccountName()}
                  </p>
                </div>
              </div>

              {/* Current Status */}
              <div style={{
                padding: '12px',
                background: daysRemaining > 0 ? 'rgba(56, 239, 125, 0.08)' : 'rgba(255, 107, 107, 0.08)',
                borderRadius: '10px',
                border: `1px solid ${daysRemaining > 0 ? 'rgba(56, 239, 125, 0.2)' : 'rgba(255, 107, 107, 0.2)'}`,
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div>
                  <div style={{ color: colors.textSecondary, fontSize: '11px', marginBottom: '2px' }}>סיום נוכחי</div>
                  <div style={{ color: colors.textPrimary, fontSize: '14px', fontWeight: 600 }}>
                    {account.trial_period_end ? moment(account.trial_period_end).format('DD/MM/YYYY') : 'לא הוגדר'}
                  </div>
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ color: colors.textSecondary, fontSize: '11px', marginBottom: '2px' }}>ימים שנותרו</div>
                  <div style={{
                    color: daysRemaining > 0 ? '#11998e' : '#ff6b6b',
                    fontSize: '18px',
                    fontWeight: 700,
                  }}>
                    {daysRemaining}
                  </div>
                </div>
              </div>

              {/* Mode Toggle */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '16px',
              }}>
                <button
                  onClick={() => setTrialMode('days')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: `1px solid ${trialMode === 'days' ? '#667eea' : colors.border}`,
                    background: trialMode === 'days' ? 'rgba(102, 126, 234, 0.1)' : colors.bgTertiary,
                    color: trialMode === 'days' ? '#667eea' : colors.textSecondary,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  הוספת ימים
                </button>
                <button
                  onClick={() => setTrialMode('date')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '8px',
                    border: `1px solid ${trialMode === 'date' ? '#667eea' : colors.border}`,
                    background: trialMode === 'date' ? 'rgba(102, 126, 234, 0.1)' : colors.bgTertiary,
                    color: trialMode === 'date' ? '#667eea' : colors.textSecondary,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  בחירת תאריך
                </button>
              </div>

              {/* Days Input or Date Picker */}
              {trialMode === 'days' ? (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    color: colors.textSecondary,
                    fontSize: '12px',
                    fontWeight: 500,
                  }}>
                    מספר ימים (שלילי להורדה)
                  </label>
                  <input
                    type="number"
                    value={trialDays}
                    onChange={(e) => setTrialDays(parseInt(e.target.value) || 0)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: `2px solid ${colors.border}`,
                      background: colors.bgTertiary,
                      color: colors.textPrimary,
                      fontSize: '14px',
                      fontWeight: 600,
                      outline: 'none',
                    }}
                  />
                </div>
              ) : (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    color: colors.textSecondary,
                    fontSize: '12px',
                    fontWeight: 500,
                  }}>
                    תאריך חדש
                  </label>
                  <input
                    type="date"
                    value={trialDate}
                    onChange={(e) => setTrialDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      borderRadius: '10px',
                      border: `2px solid ${colors.border}`,
                      background: colors.bgTertiary,
                      color: colors.textPrimary,
                      fontSize: '14px',
                      fontWeight: 600,
                      outline: 'none',
                    }}
                  />
                </div>
              )}

              {/* Reason (Required) */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  color: colors.textSecondary,
                  fontSize: '12px',
                  fontWeight: 500,
                }}>
                  סיבה לשינוי <span style={{ color: '#ff6b6b' }}>*</span>
                </label>
                <textarea
                  value={trialReason}
                  onChange={(e) => setTrialReason(e.target.value)}
                  placeholder="יש להזין סיבה לשינוי..."
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: `2px solid ${colors.border}`,
                    background: colors.bgTertiary,
                    color: colors.textPrimary,
                    fontSize: '13px',
                    outline: 'none',
                    resize: 'none',
                  }}
                />
              </div>

              {/* New Date Preview */}
              <div style={{
                padding: '12px',
                background: 'rgba(102, 126, 234, 0.08)',
                borderRadius: '10px',
                border: '1px solid rgba(102, 126, 234, 0.2)',
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div style={{ color: colors.textSecondary, fontSize: '12px' }}>תאריך חדש</div>
                <div style={{
                  color: '#667eea',
                  fontSize: '16px',
                  fontWeight: 700,
                }}>
                  {newDate.format('DD/MM/YYYY')}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowTrialModal(false)
                    setTrialReason('')
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: `1px solid ${colors.border}`,
                    background: colors.bgTertiary,
                    color: colors.textSecondary,
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                >
                  ביטול
                </button>
                <button
                  onClick={handleExtendTrial}
                  disabled={saving || !trialReason.trim()}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: 'none',
                    background: !trialReason.trim() ? 'rgba(108, 114, 147, 0.3)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    cursor: saving || !trialReason.trim() ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? 'שומר...' : 'שמור שינויים'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Transfer Confirmation Modal */}
      {showTransferConfirm && transferTarget && (
        <div
          onClick={() => { if (!transferring) setShowTransferConfirm(false) }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: colors.bgSecondary,
              borderRadius: '20px',
              padding: '24px',
              width: '400px',
              border: `1px solid ${colors.border}`,
              boxShadow: '0 24px 64px rgba(0,0,0,0.15)',
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <i className="bi bi-arrow-left-right" style={{ fontSize: '20px', color: '#fff' }}></i>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                  העברת קמפיינים
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textMuted }}>
                  פעולה זו תעביר את כל הקמפיינים
                </p>
              </div>
            </div>

            {/* Transfer visual */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              borderRadius: '12px',
              background: 'rgba(245, 87, 108, 0.04)',
              border: '1px solid rgba(245, 87, 108, 0.15)',
              marginBottom: '20px',
            }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 700,
                  margin: '0 auto 6px',
                }}>
                  {(account?.first_name || account?.email || '?')[0].toUpperCase()}
                </div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>
                  {account?.first_name} {account?.last_name}
                </div>
                <div style={{ fontSize: '10px', color: colors.textMuted }}>{account?.email}</div>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
              }}>
                <i className="bi bi-arrow-left" style={{ fontSize: '20px', color: '#f5576c' }}></i>
                <span style={{ fontSize: '9px', color: '#f5576c', fontWeight: 600 }}>כל הקמפיינים</span>
              </div>

              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 700,
                  margin: '0 auto 6px',
                }}>
                  {(transferTarget.first_name || transferTarget.email || '?')[0].toUpperCase()}
                </div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>
                  {transferTarget.first_name} {transferTarget.last_name}
                </div>
                <div style={{ fontSize: '10px', color: colors.textMuted }}>{transferTarget.email}</div>
              </div>
            </div>

            {/* Warning */}
            <div style={{
              padding: '10px 14px',
              borderRadius: '10px',
              background: 'rgba(255, 212, 59, 0.1)',
              border: '1px solid rgba(255, 212, 59, 0.3)',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <i className="bi bi-exclamation-triangle-fill" style={{ color: '#f59f00', fontSize: '16px' }}></i>
              <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                שים לב: כל הקמפיינים הפעילים יועברו לחשבון היעד. פעולה זו לא ניתנת לביטול אוטומטי.
              </span>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowTransferConfirm(false)}
                disabled={transferring}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  border: `1px solid ${colors.border}`,
                  background: colors.bgTertiary,
                  color: colors.textSecondary,
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                ביטול
              </button>
              <button
                onClick={handleTransferCampaigns}
                disabled={transferring}
                style={{
                  padding: '10px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: transferring ? 'rgba(245, 87, 108, 0.5)' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: '#fff',
                  cursor: transferring ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  opacity: transferring ? 0.8 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {transferring ? (
                  <>
                    <span className="spinner-border spinner-border-sm" />
                    מעביר...
                  </>
                ) : 'אישור העברה'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {transferToast && (
        <div style={{
          position: 'fixed',
          top: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1200,
          padding: '12px 20px',
          borderRadius: '12px',
          background: transferToast.success
            ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
            : 'linear-gradient(135deg, #ee5a5a 0%, #ff6b6b 100%)',
          color: '#fff',
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          animation: 'toastSlideIn 0.3s ease-out',
          cursor: 'pointer',
        }}
          onClick={() => setTransferToast(null)}
        >
          <i className={`bi ${transferToast.success ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}
            style={{ fontSize: '18px' }}></i>
          {transferToast.message}
        </div>
      )}

      {/* Full-disable confirmation. Lists the ACTUAL rows that will change —
          counts alone would hide, say, a virtual number the customer still
          advertises on. */}
      {disableImpact && (
        <div
          onMouseDown={(e) => { if (e.target === e.currentTarget && !disableRunning) setDisableImpact(null) }}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div dir='rtl' style={{
            background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
            maxHeight: '88vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,.35)', overflow: 'hidden',
          }}>
            <div style={{
              padding: '18px 22px', background: 'linear-gradient(135deg, #f64e60 0%, #d63384 100%)',
              color: '#fff', display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <i className='bi bi-exclamation-triangle-fill' style={{ fontSize: 20 }}></i>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700 }}>השבתת החשבון</div>
                <div style={{ fontSize: 12, opacity: .9 }}>הפעולה הזו אינה הפיכה</div>
              </div>
            </div>

            <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
              {disableImpact.already_locked && (
                <div style={{
                  background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8,
                  padding: '8px 11px', fontSize: 13, color: '#8a6d00', marginBottom: 14,
                }}>
                  ℹ️ החשבון כבר נעול. ההשבתה עדיין תשחרר פרופילים ומספרים אם נותרו כאלה.
                </div>
              )}
              {disableImpact.is_incubator && (
                <div style={{
                  background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8,
                  padding: '8px 11px', fontSize: 13, color: '#8a6d00', marginBottom: 14,
                }}>
                  ℹ️ זהו חשבון האינקובטור (מאגר הפרופילים). פרופילים לא יועברו והמפתח לא יאופס.
                </div>
              )}

              <div style={{ fontSize: 13, fontWeight: 700, color: '#3f4254', marginBottom: 10 }}>
                מה יקרה בפועל:
              </div>

              {/* profiles */}
              <div style={{ border: '1px solid #eef0f2', borderRadius: 10, padding: '11px 13px', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#181c32', marginBottom: 4 }}>
                  👤 {disableImpact.profiles_count} פרופילים מושכרים יוחזרו למאגר
                </div>
                <div style={{ fontSize: 12, color: '#7e8299', lineHeight: 1.6 }}>
                  פרופילים בבעלות החברה (fictitious) יעברו לחשבון האינקובטור ויהיו זמינים ללקוח אחר.
                </div>
                {!!disableImpact.profiles.length && (
                  <div style={{ marginTop: 7, maxHeight: 110, overflowY: 'auto', fontSize: 12, color: '#3f4254' }}>
                    {disableImpact.profiles.map((p) => (
                      <div key={p._id} style={{ padding: '2px 0' }}>
                        • {p.name || p.facebook_id || p._id}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* virtual numbers */}
              <div style={{
                border: '1px solid ' + (disableImpact.virtual_numbers_count ? '#ffcdd2' : '#eef0f2'),
                background: disableImpact.virtual_numbers_count ? '#fff5f5' : '#fff',
                borderRadius: 10, padding: '11px 13px', marginBottom: 10,
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#181c32', marginBottom: 4 }}>
                  📞 {disableImpact.virtual_numbers_count} מספרים וירטואליים ישוחררו
                </div>
                <div style={{ fontSize: 12, color: '#7e8299', lineHeight: 1.6 }}>
                  ההעברה ל-PayCall תבוטל והמספר יחזור למאגר ההקצאה — כלומר הוא עלול להינתן ללקוח אחר.
                  פוסטים קיימים שמפרסמים את המספר הזה יפסיקו להגיע ללקוח.
                </div>
                {!!disableImpact.virtual_numbers.length && (
                  <div style={{ marginTop: 7, fontSize: 12, color: '#3f4254' }}>
                    {disableImpact.virtual_numbers.map((n) => (
                      <div key={n.ddi} style={{ padding: '2px 0', direction: 'ltr', textAlign: 'right', fontFamily: 'monospace' }}>
                        {n.ddi}{n.forward_number ? ` → ${n.forward_number}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* key + lock */}
              <div style={{ border: '1px solid #eef0f2', borderRadius: 10, padding: '11px 13px', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#181c32', marginBottom: 4 }}>
                  🔑 {disableImpact.will_reset_key ? 'מפתח הקליינט יאופס' : 'מפתח הקליינט לא ישתנה'}
                </div>
                <div style={{ fontSize: 12, color: '#7e8299', lineHeight: 1.6 }}>
                  {disableImpact.will_reset_key
                    ? 'תוכנת הדסקטופ של הלקוח תתנתק מתור העבודות שלה ותפסיק לקבל משימות.'
                    : 'המפתח כבר מאופס או שזהו חשבון האינקובטור.'}
                </div>
              </div>

              <div style={{ border: '1px solid #eef0f2', borderRadius: 10, padding: '11px 13px', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#181c32' }}>
                  🔒 החשבון יינעל
                </div>
              </div>

              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#3f4254', marginBottom: 6 }}>
                לאישור, הקלידו <span style={{ color: '#f64e60' }}>השבת</span>
              </label>
              <input
                value={disableConfirmText}
                onChange={(e) => setDisableConfirmText(e.target.value)}
                placeholder='השבת'
                style={{
                  width: '100%', padding: '9px 11px', border: '1px solid #e4e6ef',
                  borderRadius: 8, fontSize: 14, outline: 'none',
                }}
              />
            </div>

            <div style={{
              display: 'flex', gap: 10, padding: '13px 22px',
              borderTop: '1px solid #eef0f2', background: '#f9f9fb',
            }}>
              <button
                onClick={handleDisableAccount}
                disabled={disableRunning || disableConfirmText.trim() !== 'השבת'}
                style={{
                  border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 700,
                  background: disableConfirmText.trim() === 'השבת' && !disableRunning ? '#f64e60' : '#d8dbe0',
                  color: '#fff',
                  cursor: disableConfirmText.trim() === 'השבת' && !disableRunning ? 'pointer' : 'not-allowed',
                }}
              >
                {disableRunning ? 'משבית…' : 'השבת את החשבון'}
              </button>
              <button
                onClick={() => setDisableImpact(null)}
                disabled={disableRunning}
                style={{
                  border: '1px solid #e4e6ef', borderRadius: 8, padding: '10px 18px',
                  fontSize: 14, fontWeight: 600, background: '#fff', color: '#5e6278',
                  cursor: disableRunning ? 'not-allowed' : 'pointer',
                }}
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      {account && <SupportChat accountId={account._id} />}
    </div>
  )
}

export default AccountDetailPage
